import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, TrendingUp, Store, Percent, Boxes, Zap, ShieldCheck, CheckCircle2,
  XCircle, Edit3, PauseCircle, Play, Undo2, Loader2, AlertTriangle, Send, RefreshCw,
  Sparkles, MessageSquare, Upload, FileSpreadsheet, FileText, FileType,
  Users, Activity, LineChart, UserPlus, Trash2, ClipboardList, Bell,
  BarChart3, Layers, Slack, Copy, MapPin, DollarSign, Camera, Phone, Rocket, Power,
  BookOpen, RotateCcw, ExternalLink, Plus,
  HelpCircle, Rows3, AlignJustify, Printer, PlayCircle, X, ChevronLeft, ChevronRight,
} from "lucide-react";
import {
  commerceSummary, commerceRecsList, commerceRecsGenerate,
  commerceRecsDecision, commerceRecsExecute, commerceRecsRollback,
  commerceCompetitors, commerceCompetitorAdd, commerceCompetitorSnapshot,
  commerceGuardrails, commerceGuardrailsUpdate,
  commerceProducts, commerceWeeklyPreview,
  commerceAskStreamUrl, commerceActions,
  commerceProductsImportCsv, commerceProductsImportFile,
  commerceInventoryHealth, commerceTeamList, commerceTeamUpsert, commerceTeamRemove,
  commerceBacktest, commerceCompetitorsChanges, commerceAuditList,
  commerceRecsAssign,
  commerceForecastSummary, commerceForecastSku, commerceBundles,
  commerceSlackWebhookGet, commerceSlackWebhookPut, commerceSlackPreview, commerceSlackSend,
  commerceCompetitorRegions, commerceAdsSummary, commerceAdsList, commerceAdsImport, commerceAdsImportCsv,
  commerceImageMatch, commerceWhatsAppSend, commerceAutonomousGet, commerceAutonomousSet,
  commerceRecPublish, commerceAdsIntegrations, commerceAdsIntegrationConnect, commerceAdsIntegrationSyncNow,
  commercePromptsList, commercePromptsAdd, commercePromptsUpdate, commercePromptsDelete,
  commerceAdsOAuthAuthorize, commerceRecPublishRollback,
} from "@/lib/api";

const TABS = [
  { key: "overview", label: "Overview", Icon: TrendingUp },
  { key: "actions", label: "Action Center", Icon: Zap },
  { key: "inventory", label: "Inventory", Icon: Boxes },
  { key: "forecasts", label: "Forecasts", Icon: BarChart3 },
  { key: "bundles", label: "Bundles", Icon: Layers },
  { key: "products", label: "Products", Icon: Boxes },
  { key: "competitors", label: "Competitors", Icon: Store },
  { key: "regions", label: "Region Gaps", Icon: MapPin },
  { key: "ads", label: "Ad Spend", Icon: DollarSign },
  { key: "guardrails", label: "Rules & Guardrails", Icon: ShieldCheck },
  { key: "team", label: "Team & Roles", Icon: Users },
  { key: "prompts", label: "Prompt Library", Icon: BookOpen },
  { key: "backtest", label: "Backtesting", Icon: LineChart },
  { key: "audit", label: "Audit Log", Icon: ClipboardList },
  { key: "report", label: "Weekly Report", Icon: Send },
];

export default function CommercePortal() {
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [density, setDensity] = useState(() =>
    (typeof window !== "undefined" && localStorage.getItem("wt_commerce_density")) || "comfortable"
  );
  const [askPrefill, setAskPrefill] = useState(null); // { text, autorun, nonce }
  const [tourOpen, setTourOpen] = useState(false);
  const refreshAll = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        commerceSummary(30),
        commerceRecsList({ status: "pending", limit: 50 }),
      ]);
      setSummary(s); setRecs(r.items || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => {
    document.title = "AI Commerce Intelligence · WebsiteTech";
    refreshAll();
    // First-run tour auto-open
    try {
      if (!localStorage.getItem("wt_commerce_tour_seen")) {
        setTimeout(() => setTourOpen(true), 900);
      }
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    try { localStorage.setItem("wt_commerce_density", density); } catch { /* ignore */ }
  }, [density]);

  const generate = async () => {
    setBusy(true);
    try { await commerceRecsGenerate(); await refreshAll(); }
    finally { setBusy(false); }
  };

  // Prompt Library → Overview Ask AI bridge
  const runPromptInAsk = (text) => {
    setAskPrefill({ text, autorun: true, nonce: Date.now() });
    setTab("overview");
    // Smooth scroll to ask panel after tab render
    setTimeout(() => {
      const el = document.querySelector('[data-testid="commerce-ask-panel"]');
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
  };

  // Guided tour: switch tab on step change
  const onTourStep = (tabKey) => { if (tabKey) setTab(tabKey); };
  const closeTour = () => {
    setTourOpen(false);
    try { localStorage.setItem("wt_commerce_tour_seen", "1"); } catch { /* ignore */ }
  };

  return (
    <div
      className="min-h-screen bg-[#fdfbf5] text-slate-900"
      data-testid="commerce-portal"
      data-density={density}
    >
      {/* Top bar */}
      <div className="sticky top-0 z-40 backdrop-blur-lg bg-[#fdfbf5]/95 border-b border-[#ebe4d3]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 grid place-items-center shadow-md shadow-emerald-500/20">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-base font-bold text-slate-900">AI Commerce Intelligence</div>
              <div className="text-xs uppercase tracking-widest text-slate-600">
                {summary ? `${summary.orders} orders · ${summary.product_count} SKUs · ${summary.period_days}d` : "loading…"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setDensity(density === "compact" ? "comfortable" : "compact")}
              data-testid="commerce-density-toggle"
              title={density === "compact" ? "Switch to comfortable spacing" : "Switch to compact spacing"}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#dcd3bc] hover:border-[#c8bd9c] bg-[#faf5e8] hover:bg-[#f5efdd] px-3 py-2 text-xs font-semibold transition"
            >
              {density === "compact" ? <AlignJustify className="w-3.5 h-3.5" /> : <Rows3 className="w-3.5 h-3.5" />}
              {density === "compact" ? "Compact" : "Comfortable"}
            </button>
            <button
              onClick={() => setTourOpen(true)}
              data-testid="commerce-take-tour"
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#dcd3bc] hover:border-emerald-400 bg-white hover:bg-emerald-50 px-3 py-2 text-xs font-semibold transition"
            >
              <HelpCircle className="w-3.5 h-3.5 text-emerald-600" /> Take tour
            </button>
            <button onClick={refreshAll} disabled={loading}
              data-testid="commerce-refresh"
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#dcd3bc] hover:border-[#c8bd9c] bg-[#faf5e8] hover:bg-[#f5efdd] px-3 py-2 text-xs font-semibold transition disabled:opacity-50">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={generate} disabled={busy}
              data-testid="commerce-generate-recs"
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900 px-3 py-2 text-xs font-bold transition disabled:opacity-60">
              <Sparkles className="w-3.5 h-3.5" /> {busy ? "Analysing…" : "Re-analyse & generate"}
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-6 flex gap-0 overflow-x-auto pb-0">
          {TABS.map(({ key, label, Icon }) => (
            <button key={key} onClick={() => setTab(key)}
              data-testid={`commerce-tab-${key}`}
              className={`inline-flex items-center gap-2 px-5 py-3.5 text-sm font-semibold whitespace-nowrap transition relative ${
                tab === key
                  ? "text-emerald-700"
                  : "text-slate-500 hover:text-slate-800"
              }`}>
              <Icon className="w-4 h-4" /> {label}
              {tab === key && (
                <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-emerald-600 rounded-full" />
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 commerce-portal-main">
        {loading && !summary && <div className="text-slate-600 text-sm">Loading…</div>}
        {tab === "overview" && summary && (
          <OverviewTab summary={summary} recs={recs} onRefresh={refreshAll} askPrefill={askPrefill} />
        )}
        {tab === "actions" && (
          <ActionCenterTab recs={recs} onRefresh={refreshAll} />
        )}
        {tab === "inventory" && <InventoryTab />}
        {tab === "forecasts" && <ForecastsTab />}
        {tab === "bundles" && <BundlesTab />}
        {tab === "products" && <ProductsTab />}
        {tab === "competitors" && <CompetitorsTab />}
        {tab === "regions" && <RegionsTab />}
        {tab === "ads" && <AdSpendTab />}
        {tab === "guardrails" && <GuardrailsTab />}
        {tab === "team" && <TeamTab />}
        {tab === "prompts" && <PromptLibraryTab onRun={runPromptInAsk} />}
        {tab === "backtest" && <BacktestTab />}
        {tab === "audit" && <AuditTab />}
        {tab === "report" && <WeeklyReportTab />}
      </div>

      {tourOpen && <GuidedTour onClose={closeTour} onStep={onTourStep} />}
    </div>
  );
}

// ============================================================================
// Overview
// ============================================================================
function OverviewTab({ summary, recs, askPrefill }) {
  const [ask, setAsk] = useState("");
  const [answer, setAnswer] = useState("");
  const [asking, setAsking] = useState(false);
  const currency = "₹";
  const fmt = (v) => `${currency} ${Math.round(v).toLocaleString("en-IN")}`;

  const askQuestion = async (overrideText) => {
    const q = (overrideText ?? ask ?? "").trim();
    if (!q || asking) return;
    setAsking(true); setAnswer("");
    try {
      const url = commerceAskStreamUrl();
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json",
                   Authorization: `Bearer ${localStorage.getItem("wt_token") || ""}` },
        body: JSON.stringify({ question: q }),
      });
      if (!res.ok || !res.body) {
        setAnswer(`Error: ${res.status}`);
        setAsking(false); return;
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const evt = JSON.parse(line.slice(5).trim());
            if (evt.delta) setAnswer((a) => a + evt.delta);
            if (evt.error) setAnswer((a) => a + `\n[error: ${evt.error}]`);
          } catch { /* ignore */ }
        }
      }
    } catch (e) {
      setAnswer(`Error: ${e.message}`);
    } finally { setAsking(false); }
  };

  // Prompt Library → Ask AI bridge: prefill + optionally auto-submit
  useEffect(() => {
    if (askPrefill && askPrefill.text) {
      setAsk(askPrefill.text);
      if (askPrefill.autorun) askQuestion(askPrefill.text);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askPrefill?.nonce]);

  return (
    <div className="space-y-6" data-testid="commerce-overview">
      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Revenue (30d)" value={fmt(summary.revenue)} />
        <Kpi label="Gross profit" value={fmt(summary.gross_profit)} accent="emerald" />
        <Kpi label="Margin" value={`${summary.gross_margin_pct}%`}
          accent={summary.gross_margin_pct >= 25 ? "emerald" : "amber"} />
        <Kpi label="Avg order value" value={fmt(summary.avg_order_value)} />
      </div>

      {/* NL Ask */}
      <div className="rounded-xl border border-emerald-400/30 bg-gradient-to-br from-emerald-500/[0.06] to-indigo-500/[0.06] p-5"
        data-testid="commerce-ask-panel">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 font-bold">
          <MessageSquare className="w-3.5 h-3.5" /> Ask AI · grounded in your data · GPT 5.6 Terra
        </div>
        <div className="mt-3 flex gap-2 flex-wrap">
          <input value={ask} onChange={(e) => setAsk(e.target.value)}
            data-testid="commerce-ask-input"
            onKeyDown={(e) => e.key === "Enter" && askQuestion()}
            placeholder="e.g. Which products should I discount this week?"
            className="flex-1 min-w-[240px] rounded-lg bg-white border border-[#ebe4d3] focus:border-emerald-400 outline-none px-3 py-2.5 text-sm" />
          <button onClick={askQuestion} disabled={asking || !ask.trim()}
            data-testid="commerce-ask-submit"
            className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-900 rounded-lg px-4 py-2.5 text-sm font-bold transition">
            {asking ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> …</> : <>Ask <ArrowRight className="w-3.5 h-3.5" /></>}
          </button>
        </div>
        <div className="mt-3 flex gap-2 flex-wrap">
          {["Which products are selling well but losing margin?",
            "Which inventory is at risk?",
            "What are today's top 3 actions?"].map((s) => (
            <button key={s} onClick={() => setAsk(s)}
              className="text-[11px] text-emerald-700 hover:text-emerald-200 border border-emerald-400/20 hover:border-emerald-400/50 bg-emerald-500/5 rounded-full px-2 py-1">
              {s}
            </button>
          ))}
        </div>
        {answer && (
          <div className="mt-4 rounded-lg bg-slate-900/80 border border-[#ebe4d3] p-4 text-sm whitespace-pre-wrap leading-relaxed"
            data-testid="commerce-ask-answer">
            {answer}
          </div>
        )}
      </div>

      {/* Top products + Inventory risks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-[#ebe4d3] bg-white shadow-sm p-5">
          <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Top products (30d)</div>
          <div className="mt-4 space-y-2">
            {summary.top_products.slice(0, 6).map((p) => (
              <div key={p.sku} className="flex items-center gap-3 border-b border-[#f0e9d6] pb-2"
                data-testid={`commerce-top-product-${p.sku}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{p.title}</div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">{p.category} · SKU {p.sku}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-mono">{fmt(p.revenue)}</div>
                  <div className={`text-[10px] font-bold ${p.margin_pct >= 25 ? "text-emerald-700" : "text-amber-700"}`}>
                    {p.margin_pct}% margin
                  </div>
                </div>
              </div>
            ))}
            {summary.top_products.length === 0 && (
              <div className="text-sm text-slate-500">No orders yet — import products and orders to see top performers.</div>
            )}
          </div>
        </div>
        <div className="rounded-2xl border border-[#ebe4d3] bg-white shadow-sm p-5">
          <div className="text-xs uppercase tracking-widest text-amber-700 font-bold flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3" /> Inventory at risk
          </div>
          <div className="mt-4 space-y-2">
            {summary.low_stock.slice(0, 6).map((p) => (
              <div key={p.sku} className="flex items-center gap-3 border-b border-[#f0e9d6] pb-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{p.title}</div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">SKU {p.sku}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-amber-700 font-mono">{p.stock} in stock</div>
                  {p.days_of_supply !== null && (
                    <div className="text-[10px] text-slate-600">{p.days_of_supply}d supply</div>
                  )}
                </div>
              </div>
            ))}
            {summary.low_stock.length === 0 && (
              <div className="text-sm text-slate-600 flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Stock levels look healthy.</div>
            )}
          </div>
        </div>
      </div>

      {/* Action-Center preview */}
      <div className="rounded-2xl border border-[#ebe4d3] bg-white shadow-sm p-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Top pending actions</div>
          <div className="text-xs text-slate-600">{recs.length} awaiting review</div>
        </div>
        <div className="mt-4 space-y-2">
          {recs.slice(0, 3).map((r) => (
            <div key={r.id} className="rounded-lg border border-[#ebe4d3] bg-slate-900/40 p-3">
              <div className="text-sm font-semibold">{r.title}</div>
              <div className="text-xs text-slate-600 mt-1">{r.why}</div>
            </div>
          ))}
          {recs.length === 0 && (
            <div className="text-sm text-slate-500">Nothing pending — hit "Re-analyse & generate" after your next data update.</div>
          )}
        </div>
      </div>
    </div>
  );
}


function Kpi({ label, value, accent = "slate" }) {
  const color = {
    slate: "text-slate-900", emerald: "text-emerald-700",
    amber: "text-amber-700", rose: "text-rose-700",
    indigo: "text-indigo-700", fuchsia: "text-fuchsia-700",
  }[accent] || "text-slate-900";
  const ring = {
    slate: "", emerald: "ring-emerald-500/10",
    amber: "ring-amber-500/10", rose: "ring-rose-500/10",
    indigo: "ring-indigo-500/10", fuchsia: "ring-fuchsia-500/10",
  }[accent] || "";
  return (
    <div className={`kpi-card rounded-2xl border border-[#ebe4d3] bg-white p-6 shadow-sm hover:shadow-md transition-shadow ${ring ? "ring-1 " + ring : ""}`}>
      <div className="kpi-label text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</div>
      <div className={`kpi-value mt-2 text-3xl font-bold tracking-tight ${color}`}>{value}</div>
    </div>
  );
}


// ============================================================================
// Action Center
// ============================================================================
function ActionCenterTab({ recs, onRefresh }) {
  const [filterKind, setFilterKind] = useState("all");
  const [busyId, setBusyId] = useState(null);
  const [note, setNote] = useState("");
  const [editing, setEditing] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [whatsAppFor, setWhatsAppFor] = useState(null);
  const [waPhone, setWaPhone] = useState("");
  const [team, setTeam] = useState([]);
  useEffect(() => { commerceTeamList().then((d) => setTeam(d.items || [])).catch(() => {}); }, []);
  const kinds = useMemo(() => {
    const s = new Set(recs.map((r) => r.kind));
    return ["all", ...Array.from(s)];
  }, [recs]);
  const filtered = filterKind === "all" ? recs : recs.filter((r) => r.kind === filterKind);

  const decide = async (r, decision, modified_price) => {
    // Client-side gate — matches server enforcement so users get a fast, kind error
    if ((decision === "reject" || decision === "modify") && !(note && note.trim().length >= 3)) {
      alert(`Please add a short reason (min 3 chars) before you ${decision} this recommendation — it teaches the engine what to do differently.`);
      return;
    }
    setBusyId(r.id);
    try {
      await commerceRecsDecision(r.id, { decision, modified_price, note: note || null });
      if (decision === "approve" || decision === "modify") {
        await commerceRecsExecute(r.id);
      }
      setNote(""); setEditing(null);
      await onRefresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Decision failed");
    } finally { setBusyId(null); }
  };

  const assign = async (r, email) => {
    if (!email) return;
    try {
      await commerceRecsAssign(r.id, { assignee_email: email, note: note || null });
      setAssigning(null); await onRefresh();
    } catch (e) { alert(e?.response?.data?.detail || "Assign failed"); }
  };

  const sendWhatsApp = async (r) => {
    const phone = waPhone.trim();
    if (!phone) { alert("Enter a phone number"); return; }
    try {
      const res = await commerceWhatsAppSend(r.id, phone);
      alert(res.mocked
        ? `Approval request queued (MOCKED — Twilio not configured). Outbox id: ${res.outbox_id}`
        : `WhatsApp approval request sent via ${res.provider}. Outbox id: ${res.outbox_id}`);
      setWhatsAppFor(null); setWaPhone("");
    } catch (e) { alert(e?.response?.data?.detail || "Failed to send"); }
  };

  const publish = async (r) => {
    if (!window.confirm(`Publish new price ₹${r.recommended_price} for ${r.sku} to Shopify + WooCommerce?`)) return;
    setBusyId(r.id);
    try {
      const res = await commerceRecPublish(r.id);
      const ok = res.results.filter((x) => x.published).map((x) => x.channel);
      const err = res.results.filter((x) => !x.published).map((x) => x.reason);
      alert(`Publish result: ${ok.length ? "OK on " + ok.join(", ") : "none"}${err.length ? " · Errors: " + err.join(", ") : ""}`);
      await onRefresh();
    } catch (e) { alert(e?.response?.data?.detail || "Publish failed"); }
    finally { setBusyId(null); }
  };

  const rollbackPublish = async (r) => {
    if (!window.confirm(`Roll back the last publish and restore the previous price on Shopify + WooCommerce?`)) return;
    setBusyId(r.id);
    try {
      const res = await commerceRecPublishRollback(r.id);
      const ok = res.results.filter((x) => x.published).map((x) => x.channel);
      alert(res.any_rolled_back
        ? `Restored ₹${res.restored_price} on ${ok.join(", ")}`
        : "Nothing rolled back — check publish errors.");
      await onRefresh();
    } catch (e) { alert(e?.response?.data?.detail || "Rollback failed"); }
    finally { setBusyId(null); }
  };

  return (
    <div className="space-y-4" data-testid="commerce-action-center">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="text-xs uppercase tracking-widest text-slate-600 font-bold">Filter</div>
        {kinds.map((k) => (
          <button key={k} onClick={() => setFilterKind(k)}
            className={`text-xs rounded-full px-3 py-1 font-semibold transition ${
              filterKind === k ? "bg-emerald-500 text-slate-900" : "bg-[#faf5e8] hover:bg-[#f5efdd] text-slate-700"
            }`}>{k}</button>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="rounded-2xl border border-[#ebe4d3] bg-white shadow-sm p-8 text-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" />
          <div className="mt-3 text-lg font-bold">Inbox zero — nothing to review.</div>
          <div className="mt-1 text-sm text-slate-600">Hit "Re-analyse & generate" after your next sync to surface fresh actions.</div>
        </div>
      )}

      {filtered.map((r) => (
        <div key={r.id} className="rounded-2xl border border-[#ebe4d3] bg-white shadow-sm p-5"
          data-testid={`commerce-rec-${r.id}`}>
          <div className="flex flex-wrap items-start gap-3">
            <KindPill kind={r.kind} />
            <div className="text-xs uppercase tracking-widest text-slate-600 font-bold">
              confidence · {(r.confidence * 100).toFixed(0)}%
            </div>
            <div className="ml-auto text-xs text-slate-600 font-mono">SKU {r.sku}</div>
          </div>
          <div className="mt-3 text-xl font-bold">{r.title}</div>
          <div className="mt-1 text-sm text-slate-700">{r.why}</div>

          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniStat label="Current price" value={`₹${r.current_price.toFixed(2)}`} />
            <MiniStat label="Recommended" value={`₹${r.recommended_price.toFixed(2)}`} accent="emerald" />
            <MiniStat label="Change" value={`${r.recommended_change_pct > 0 ? "+" : ""}${r.recommended_change_pct}%`}
              accent={r.recommended_change_pct >= 0 ? "emerald" : "rose"} />
            {r.expected_impact?.gross_profit_delta !== undefined && (
              <MiniStat label="Est. profit Δ (30d)"
                value={`₹${Math.round(r.expected_impact.gross_profit_delta).toLocaleString("en-IN")}`}
                accent={r.expected_impact.gross_profit_delta >= 0 ? "emerald" : "rose"} />
            )}
          </div>

          {r.signals && (
            <details className="mt-4">
              <summary className="cursor-pointer text-xs font-semibold text-slate-600 hover:text-white">Supporting signals</summary>
              <pre className="mt-2 text-[11px] text-slate-700 bg-[#faf5e8] border border-[#ebe4d3] rounded p-3 overflow-auto">
{JSON.stringify(r.signals, null, 2)}</pre>
            </details>
          )}

          {editing === r.id && (
            <div className="mt-4 rounded-lg bg-[#faf5e8] border border-emerald-400/30 p-3 space-y-2">
              <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Modify price before approving</div>
              <input id={`mod-${r.id}`} type="number" step="0.01" min="0"
                defaultValue={r.recommended_price}
                data-testid={`commerce-rec-modify-input-${r.id}`}
                className="w-full rounded bg-white border border-[#ebe4d3] focus:border-emerald-400 outline-none px-3 py-2 text-sm" />
              <div className="flex gap-2">
                <button onClick={() => {
                  const v = parseFloat(document.getElementById(`mod-${r.id}`).value);
                  if (v > 0) decide(r, "modify", v);
                }} disabled={busyId === r.id}
                  data-testid={`commerce-rec-modify-approve-${r.id}`}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-900 rounded px-3 py-2 text-xs font-bold">
                  Modify & approve
                </button>
                <button onClick={() => setEditing(null)}
                  className="rounded border border-[#dcd3bc] hover:border-[#c8bd9c] px-3 py-2 text-xs font-semibold">Cancel</button>
              </div>
            </div>
          )}

          <input value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Reason / note (required to reject or modify)"
            data-testid={`commerce-rec-note-${r.id}`}
            className="mt-4 w-full rounded bg-white border border-[#ebe4d3] focus:border-emerald-400 outline-none px-3 py-2 text-xs" />

          {r.assignee_email && (
            <div className="mt-2 text-[11px] text-indigo-700"
              data-testid={`commerce-rec-assignee-${r.id}`}>
              Assigned to <strong>{r.assignee_email}</strong>
            </div>
          )}

          {assigning === r.id && (
            <div className="mt-3 rounded-lg bg-indigo-500/10 border border-indigo-400/30 p-3 space-y-2"
              data-testid={`commerce-rec-assign-panel-${r.id}`}>
              <div className="text-xs uppercase tracking-widest text-indigo-700 font-bold">Assign to a teammate</div>
              <select id={`assign-${r.id}`} defaultValue=""
                className="w-full rounded bg-white border border-[#ebe4d3] focus:border-indigo-400 outline-none px-3 py-2 text-sm">
                <option value="" disabled>— pick an approver —</option>
                {team.filter((m) => m.role !== "viewer").map((m) => (
                  <option key={m.email} value={m.email}>{m.display_name || m.email} · {m.role}</option>
                ))}
              </select>
              <div className="flex gap-2">
                <button onClick={() => {
                  const v = document.getElementById(`assign-${r.id}`).value;
                  if (v) assign(r, v);
                }}
                  data-testid={`commerce-rec-assign-confirm-${r.id}`}
                  className="flex-1 bg-indigo-500 hover:bg-indigo-400 text-white rounded px-3 py-2 text-xs font-bold">
                  Assign
                </button>
                <button onClick={() => setAssigning(null)}
                  className="rounded border border-[#dcd3bc] hover:border-[#c8bd9c] px-3 py-2 text-xs font-semibold">Cancel</button>
              </div>
            </div>
          )}
          {whatsAppFor === r.id && (
            <div className="mt-3 rounded-lg bg-green-500/10 border border-green-400/30 p-3 space-y-2"
              data-testid={`commerce-rec-wa-panel-${r.id}`}>
              <div className="text-xs uppercase tracking-widest text-green-700 font-bold">WhatsApp approval request</div>
              <input value={waPhone} onChange={(e) => setWaPhone(e.target.value)}
                data-testid={`commerce-rec-wa-phone-${r.id}`}
                placeholder="+91 98…"
                className="w-full rounded bg-white border border-[#ebe4d3] focus:border-green-400 outline-none px-3 py-2 text-sm font-mono" />
              <div className="text-[10px] text-green-200/70">
                Approver replies "approve {r.id.slice(0, 8)}" to accept, or "reject {r.id.slice(0, 8)} note=your reason" to decline.
              </div>
              <div className="flex gap-2">
                <button onClick={() => sendWhatsApp(r)}
                  data-testid={`commerce-rec-wa-send-${r.id}`}
                  className="flex-1 bg-green-500 hover:bg-green-400 text-slate-900 rounded px-3 py-2 text-xs font-bold">
                  Send request
                </button>
                <button onClick={() => { setWhatsAppFor(null); setWaPhone(""); }}
                  className="rounded border border-[#dcd3bc] hover:border-[#c8bd9c] px-3 py-2 text-xs font-semibold">Cancel</button>
              </div>
            </div>
          )}

          <div className="mt-3 flex gap-2 flex-wrap">
            {r.kind !== "inventory-reorder" && (
              <>
                <button onClick={() => decide(r, "approve")} disabled={busyId === r.id}
                  data-testid={`commerce-rec-approve-${r.id}`}
                  className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-900 px-3 py-2 rounded font-bold text-xs transition">
                  {busyId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Approve & execute
                </button>
                <button onClick={() => setEditing(r.id)}
                  data-testid={`commerce-rec-modify-${r.id}`}
                  className="inline-flex items-center gap-1.5 border border-emerald-400/40 hover:border-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200 px-3 py-2 rounded font-semibold text-xs transition">
                  <Edit3 className="w-3.5 h-3.5" /> Modify
                </button>
              </>
            )}
            {r.kind === "inventory-reorder" && (
              <button onClick={() => decide(r, "approve")} disabled={busyId === r.id}
                data-testid={`commerce-rec-ack-${r.id}`}
                className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-900 px-3 py-2 rounded font-bold text-xs transition">
                <CheckCircle2 className="w-3.5 h-3.5" /> Acknowledge
              </button>
            )}
            <button onClick={() => decide(r, "reject")} disabled={busyId === r.id}
              data-testid={`commerce-rec-reject-${r.id}`}
              className="inline-flex items-center gap-1.5 border border-[#dcd3bc] hover:border-rose-400/60 bg-[#faf5e8] hover:bg-rose-500/10 text-slate-700 hover:text-rose-200 px-3 py-2 rounded font-semibold text-xs transition">
              <XCircle className="w-3.5 h-3.5" /> Reject
            </button>
            <button onClick={() => decide(r, "snooze")} disabled={busyId === r.id}
              data-testid={`commerce-rec-snooze-${r.id}`}
              className="inline-flex items-center gap-1.5 border border-[#dcd3bc] hover:border-[#c8bd9c] bg-[#faf5e8] hover:bg-[#f5efdd] text-slate-700 px-3 py-2 rounded font-semibold text-xs transition">
              <PauseCircle className="w-3.5 h-3.5" /> Snooze 7d
            </button>
            <button onClick={() => setAssigning(r.id)} disabled={busyId === r.id}
              data-testid={`commerce-rec-assign-${r.id}`}
              className="inline-flex items-center gap-1.5 border border-indigo-400/40 hover:border-indigo-400 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-200 px-3 py-2 rounded font-semibold text-xs transition">
              <UserPlus className="w-3.5 h-3.5" /> Assign
            </button>
            <button onClick={() => setWhatsAppFor(r.id)} disabled={busyId === r.id}
              data-testid={`commerce-rec-wa-${r.id}`}
              className="inline-flex items-center gap-1.5 border border-green-400/40 hover:border-green-400 bg-green-500/10 hover:bg-green-500/20 text-green-200 px-3 py-2 rounded font-semibold text-xs transition">
              <Phone className="w-3.5 h-3.5" /> WhatsApp
            </button>
            {(r.status === "approved" || r.status === "executed") && (
              <button onClick={() => publish(r)} disabled={busyId === r.id}
                data-testid={`commerce-rec-publish-${r.id}`}
                className="inline-flex items-center gap-1.5 border border-fuchsia-400/40 hover:border-fuchsia-400 bg-fuchsia-500/10 hover:bg-fuchsia-500/20 text-fuchsia-200 px-3 py-2 rounded font-semibold text-xs transition">
                <Rocket className="w-3.5 h-3.5" /> Publish to store
              </button>
            )}
            {r.published_at && !r.rollback_at && (
              <button onClick={() => rollbackPublish(r)} disabled={busyId === r.id}
                data-testid={`commerce-rec-rollback-publish-${r.id}`}
                className="inline-flex items-center gap-1.5 border border-orange-400/40 hover:border-orange-400 bg-orange-500/10 hover:bg-orange-500/20 text-orange-200 px-3 py-2 rounded font-semibold text-xs transition">
                <RotateCcw className="w-3.5 h-3.5" /> Rollback publish
              </button>
            )}
            {r.rollback_at && (
              <span className="inline-flex items-center gap-1.5 border border-slate-400/30 bg-[#faf5e8]0/10 text-slate-700 px-3 py-2 rounded font-semibold text-xs"
                data-testid={`commerce-rec-rolled-back-${r.id}`}>
                Rolled back ₹{r.rolled_back_price?.toFixed?.(2)}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}


function KindPill({ kind }) {
  const map = {
    "margin-floor": ["bg-amber-500/15 text-amber-700 border-amber-400/30", "Margin protect"],
    "competitor-match": ["bg-indigo-500/15 text-indigo-700 border-indigo-400/30", "Competitor"],
    "clear-aging-stock": ["bg-rose-500/15 text-rose-700 border-rose-400/30", "Clear stock"],
    "inventory-reorder": ["bg-emerald-500/15 text-emerald-700 border-emerald-400/30", "Reorder"],
  };
  const [cls, label] = map[kind] || ["bg-white/10 text-slate-800 border-[#dcd3bc]", kind];
  return (
    <div className={`text-[10px] uppercase tracking-widest font-bold rounded px-2 py-1 border ${cls}`}>{label}</div>
  );
}

function MiniStat({ label, value, accent = "slate" }) {
  const color = { slate: "text-slate-900", emerald: "text-emerald-700",
                    rose: "text-rose-700", amber: "text-amber-700",
                    indigo: "text-indigo-700" }[accent] || "text-slate-900";
  return (
    <div className="rounded-lg bg-[#faf5e8] border border-[#ebe4d3] p-3">
      <div className="text-[11px] uppercase tracking-widest text-slate-600 font-bold">{label}</div>
      <div className={`mt-1 text-base font-bold font-mono ${color}`}>{value}</div>
    </div>
  );
}


// ============================================================================
// Products (simple list + inline add)
// ============================================================================
function ProductsTab() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const reload = () => commerceProducts({ q, limit: 200 }).then((d) => setRows(d.items || []));
  useEffect(() => { reload(); }, [q]);
  return (
    <div className="space-y-4" data-testid="commerce-products">
      <ImportPanel onImported={reload} />
      <div className="flex gap-2 items-center flex-wrap">
        <input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search products by SKU or title…"
          data-testid="commerce-products-search"
          className="flex-1 min-w-[240px] rounded-lg bg-white border border-[#ebe4d3] focus:border-emerald-400 outline-none px-3 py-2.5 text-sm" />
        <div className="text-xs text-slate-600">{rows.length} products</div>
      </div>
      <div className="rounded-2xl border border-[#ebe4d3] bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#faf5e8] text-xs uppercase tracking-widest text-slate-600 font-bold">
            <tr>
              <th className="text-left px-4 py-3">Product</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-right px-4 py-3">Cost</th>
              <th className="text-right px-4 py-3">Price</th>
              <th className="text-right px-4 py-3">Margin</th>
              <th className="text-right px-4 py-3">Stock</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => {
              const margin = p.cost ? ((p.price - p.cost) / p.price * 100).toFixed(1) : "—";
              return (
                <tr key={p.id} className="border-t border-[#f0e9d6] hover:bg-white/[0.02]">
                  <td className="px-4 py-3">
                    <div className="font-semibold">{p.title}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{p.sku}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{p.category || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-600">{p.cost ? `₹${p.cost.toFixed(2)}` : "—"}</td>
                  <td className="px-4 py-3 text-right font-mono">₹{p.price.toFixed(2)}</td>
                  <td className={`px-4 py-3 text-right font-mono ${margin === "—" ? "text-slate-500" : parseFloat(margin) >= 20 ? "text-emerald-700" : "text-amber-700"}`}>
                    {margin}{margin !== "—" && "%"}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{p.stock}</td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No products yet. Import a CSV, XLSX or PDF price list above.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function ImportPanel({ onImported }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setError(""); setResult(null);
    try {
      const name = file.name.toLowerCase();
      if (name.endsWith(".csv")) {
        const text = await file.text();
        const r = await commerceProductsImportCsv(text);
        setResult(r);
      } else if (name.endsWith(".xlsx") || name.endsWith(".xlsm")) {
        const b64 = await _fileToB64(file);
        const r = await commerceProductsImportFile("xlsx", b64, file.name);
        setResult(r);
      } else if (name.endsWith(".pdf")) {
        const b64 = await _fileToB64(file);
        const r = await commerceProductsImportFile("pdf", b64, file.name);
        setResult(r);
      } else {
        setError("Unsupported file type. Please upload CSV, XLSX or PDF.");
      }
      onImported && onImported();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Import failed");
    } finally { setBusy(false); e.target.value = ""; }
  };

  return (
    <div className="rounded-xl border border-emerald-400/25 bg-gradient-to-br from-emerald-500/[0.06] to-transparent p-5"
      data-testid="commerce-import-panel">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold flex items-center gap-1.5">
            <Upload className="w-3.5 h-3.5" /> Import your inventory + pricing
          </div>
          <div className="mt-1 text-sm text-slate-700">
            Drop a <strong className="text-white">CSV</strong>, <strong className="text-white">XLSX</strong> or <strong className="text-white">PDF</strong> price list. We accept flexible headers (SKU / Code, Title / Name, Price / MRP, Cost, Stock, etc.).
          </div>
          <div className="mt-2 flex flex-wrap gap-2 text-[10px] text-slate-600">
            <span className="inline-flex items-center gap-1"><FileText className="w-3 h-3" /> CSV</span>
            <span className="inline-flex items-center gap-1"><FileSpreadsheet className="w-3 h-3" /> XLSX</span>
            <span className="inline-flex items-center gap-1"><FileType className="w-3 h-3" /> PDF (tables only)</span>
          </div>
        </div>
        <label className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-900 rounded-lg px-4 py-2.5 font-bold text-sm transition cursor-pointer"
          data-testid="commerce-import-file">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {busy ? "Importing…" : "Choose file"}
          <input type="file" accept=".csv,.xlsx,.xlsm,.pdf" className="hidden" onChange={handleFile} disabled={busy} />
        </label>
      </div>
      {result && (
        <div className="mt-3 rounded-lg bg-emerald-500/10 border border-emerald-400/30 p-3 text-sm text-emerald-100"
          data-testid="commerce-import-result">
          <CheckCircle2 className="inline w-4 h-4 mr-1" />
          Imported <strong>{result.imported}</strong> product{result.imported === 1 ? "" : "s"}
          {result.filename ? ` from ${result.filename}` : ""}
          {result.rows_seen ? ` (${result.rows_seen} rows scanned)` : ""}
          {result.note && <div className="mt-2 text-xs text-amber-200">{result.note}</div>}
          {result.errors && result.errors.length > 0 && (
            <details className="mt-2 text-xs text-amber-200">
              <summary>{result.errors.length} row error(s)</summary>
              <ul className="mt-1 space-y-0.5">
                {result.errors.slice(0, 10).map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}
      {error && (
        <div className="mt-3 rounded-lg bg-rose-500/10 border border-rose-400/30 p-3 text-sm text-rose-200"
          data-testid="commerce-import-error">
          <XCircle className="inline w-4 h-4 mr-1" /> {error}
        </div>
      )}
    </div>
  );
}


async function _fileToB64(file) {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}


// ============================================================================
// Competitors
// ============================================================================
function CompetitorsTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", url: "", matched_sku: "" });
  const [snap, setSnap] = useState({ id: null, price: "" });
  const load = () => commerceCompetitors().then((d) => setRows(d.items || []));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name || !form.url) return;
    await commerceCompetitorAdd(form);
    setForm({ name: "", url: "", matched_sku: "" }); load();
  };
  const submitSnap = async (id) => {
    if (!snap.price) return;
    await commerceCompetitorSnapshot({ competitor_id: id, price: parseFloat(snap.price) });
    setSnap({ id: null, price: "" }); load();
  };

  return (
    <div className="space-y-4" data-testid="commerce-competitors">
      <ImageMatchPanel onMatched={load} />
      <div className="rounded-2xl border border-[#ebe4d3] bg-white shadow-sm p-5">
        <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Add a competitor product URL</div>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-2">
          <input placeholder="Competitor name" value={form.name}
            data-testid="commerce-comp-name"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="rounded bg-white border border-[#ebe4d3] focus:border-emerald-400 outline-none px-3 py-2.5 text-sm" />
          <input placeholder="Product URL" value={form.url}
            data-testid="commerce-comp-url"
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            className="md:col-span-2 rounded bg-white border border-[#ebe4d3] focus:border-emerald-400 outline-none px-3 py-2.5 text-sm" />
          <input placeholder="Match my SKU (optional)" value={form.matched_sku}
            data-testid="commerce-comp-sku"
            onChange={(e) => setForm({ ...form, matched_sku: e.target.value })}
            className="rounded bg-white border border-[#ebe4d3] focus:border-emerald-400 outline-none px-3 py-2.5 text-sm" />
        </div>
        <div className="mt-3">
          <button onClick={add}
            data-testid="commerce-comp-add"
            className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-slate-900 rounded px-4 py-2 text-xs font-bold">
            Add competitor <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-[#ebe4d3] bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#faf5e8] text-xs uppercase tracking-widest text-slate-600 font-bold">
            <tr>
              <th className="text-left px-4 py-3">Competitor</th>
              <th className="text-left px-4 py-3">Matched SKU</th>
              <th className="text-left px-4 py-3">Match</th>
              <th className="text-right px-4 py-3">Latest snapshot</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#f0e9d6]"
                data-testid={`commerce-comp-row-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold">{r.name}</div>
                  <a href={r.url} target="_blank" rel="noopener noreferrer"
                    className="text-[11px] text-emerald-700 hover:underline break-all">{r.url}</a>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{r.matched_sku || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs uppercase tracking-widest font-bold rounded px-2 py-0.5 ${r.verified
                      ? "bg-emerald-500/15 text-emerald-700 border border-emerald-400/30"
                      : "bg-amber-500/15 text-amber-700 border border-amber-400/30"}`}>
                    {r.verified ? "verified" : `${r.match_type} · ${(r.match_confidence * 100).toFixed(0)}%`}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  {r.latest ? (
                    <>
                      <div className="font-mono">₹{r.latest.price.toFixed(2)}</div>
                      <div className="text-[10px] text-slate-500">{new Date(r.latest.captured_at).toLocaleDateString()}</div>
                    </>
                  ) : <span className="text-slate-500">No data</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  {snap.id === r.id ? (
                    <div className="inline-flex gap-1">
                      <input type="number" step="0.01" value={snap.price}
                        onChange={(e) => setSnap({ ...snap, price: e.target.value })}
                        placeholder="Price"
                        className="w-24 rounded bg-white border border-[#dcd3bc] outline-none px-2 py-1 text-xs" />
                      <button onClick={() => submitSnap(r.id)}
                        className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 rounded px-2 py-1 text-[11px] font-bold">Save</button>
                      <button onClick={() => setSnap({ id: null, price: "" })}
                        className="border border-[#dcd3bc] rounded px-2 py-1 text-[11px]">×</button>
                    </div>
                  ) : (
                    <button onClick={() => setSnap({ id: r.id, price: r.latest?.price?.toString() || "" })}
                      data-testid={`commerce-comp-snap-${r.id}`}
                      className="text-[11px] font-bold text-emerald-700 hover:text-emerald-200">+ price</button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">No competitors yet. Add one above.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ============================================================================
// Guardrails
// ============================================================================
function GuardrailsTab() {
  const [g, setG] = useState(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => { commerceGuardrails().then(setG); }, []);
  const save = async () => {
    const upd = await commerceGuardrailsUpdate(g);
    setG(upd); setSaved(true); setTimeout(() => setSaved(false), 2500);
  };
  if (!g) return <div className="text-slate-600 text-sm">Loading…</div>;
  return (
    <div className="max-w-2xl space-y-5" data-testid="commerce-guardrails">
      <AutonomousPanel />
      <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/5 p-4 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-slate-800">
          The MVP requires human approval for every action. That toggle is locked on in this release —
          it will become configurable when we ship autonomous price publishing in a later phase.
        </div>
      </div>

      <FieldNumber label="Minimum gross margin (%)" value={g.min_margin_pct}
        onChange={(v) => setG({ ...g, min_margin_pct: v })} step="0.5" />
      <FieldNumber label="Minimum contribution margin (%)" value={g.min_contribution_margin_pct ?? 0}
        onChange={(v) => setG({ ...g, min_contribution_margin_pct: v })} step="0.5" />
      <FieldNumber label="Maximum change per action (%)" value={g.max_change_pct}
        onChange={(v) => setG({ ...g, max_change_pct: v })} step="0.5" />
      <FieldNumber label="Maximum discount depth on any promotion (%)" value={g.max_discount_pct ?? 40}
        onChange={(v) => setG({ ...g, max_discount_pct: v })} step="1" />
      <FieldNumber label="Maximum price changes per week" value={g.max_changes_per_week}
        onChange={(v) => setG({ ...g, max_changes_per_week: v })} step="1" />
      <FieldNumber label="Extra confirm required when change exceeds (%)"
        value={g.mfa_required_over_pct ?? 25}
        onChange={(v) => setG({ ...g, mfa_required_over_pct: v })} step="0.5" />
      <FieldNumber label="Minimum price (optional)" value={g.min_price || ""}
        onChange={(v) => setG({ ...g, min_price: v || null })} step="0.01" />
      <FieldNumber label="Maximum price (optional)" value={g.max_price || ""}
        onChange={(v) => setG({ ...g, max_price: v || null })} step="0.01" />

      <FieldCsv label="Protected SKUs (comma-separated — engine will skip these)"
        value={(g.protected_skus || []).join(", ")}
        onChange={(v) => setG({ ...g, protected_skus: v.split(",").map((s) => s.trim()).filter(Boolean) })}
        testId="commerce-guardrail-protected-skus" />
      <FieldCsv label="MAP-restricted SKUs (brand policy — never lower)"
        value={(g.map_restricted_skus || []).join(", ")}
        onChange={(v) => setG({ ...g, map_restricted_skus: v.split(",").map((s) => s.trim()).filter(Boolean) })}
        testId="commerce-guardrail-map-skus" />
      <FieldCsv label="Blackout dates (YYYY-MM-DD, comma-separated — no new recs on these)"
        value={(g.blackout_dates || []).join(", ")}
        onChange={(v) => setG({ ...g, blackout_dates: v.split(",").map((s) => s.trim()).filter(Boolean) })}
        testId="commerce-guardrail-blackout" />
      <FieldCsv label="Excluded categories"
        value={(g.excluded_categories || []).join(", ")}
        onChange={(v) => setG({ ...g, excluded_categories: v.split(",").map((s) => s.trim()).filter(Boolean) })}
        testId="commerce-guardrail-excluded-cats" />
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-600 font-bold mb-1.5">Rounding rule</div>
        <select value={g.rounding} onChange={(e) => setG({ ...g, rounding: e.target.value })}
          data-testid="commerce-guardrail-rounding"
          className="w-full rounded bg-white border border-[#ebe4d3] focus:border-emerald-400 outline-none px-3 py-2.5 text-sm">
          <option value="psychological-99">.99 ending (e.g. 199.99)</option>
          <option value="psychological-95">.95 ending</option>
          <option value="whole">Whole numbers</option>
          <option value="none">No rounding</option>
        </select>
      </div>
      <button onClick={save}
        data-testid="commerce-guardrails-save"
        className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-slate-900 rounded px-4 py-2.5 text-sm font-bold">
        Save rules {saved && <CheckCircle2 className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}


function FieldNumber({ label, value, onChange, step = "0.5" }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-slate-600 font-bold mb-1.5">{label}</div>
      <input type="number" step={step} value={value}
        onChange={(e) => onChange(e.target.value === "" ? "" : parseFloat(e.target.value))}
        className="w-full rounded bg-white border border-[#ebe4d3] focus:border-emerald-400 outline-none px-3 py-2.5 text-sm" />
    </div>
  );
}


function FieldCsv({ label, value, onChange, testId }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-slate-600 font-bold mb-1.5">{label}</div>
      <input type="text" value={value}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Leave empty for none"
        className="w-full rounded bg-white border border-[#ebe4d3] focus:border-emerald-400 outline-none px-3 py-2.5 text-sm font-mono" />
    </div>
  );
}


// ============================================================================
// Weekly Report
// ============================================================================
function WeeklyReportTab() {
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { commerceWeeklyPreview().then((d) => { setRep(d); setLoading(false); }); }, []);
  if (loading) return <div className="text-slate-600 text-sm">Building report…</div>;
  if (!rep) return null;
  const fmt = (v) => `₹${Math.round(v).toLocaleString("en-IN")}`;
  const printedAt = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  const doPrint = () => {
    // Slight defer so any late paint completes before the print dialog opens
    setTimeout(() => window.print(), 60);
  };

  return (
    <div className="max-w-3xl space-y-6 commerce-report-print" data-testid="commerce-report">
      <div className="flex items-center justify-between flex-wrap gap-3 print-hide">
        <div className="text-xs uppercase tracking-widest text-slate-600 font-semibold">Weekly digest · client-ready</div>
        <button
          onClick={doPrint}
          data-testid="commerce-report-print"
          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900 px-4 py-2 text-xs font-bold transition"
        >
          <Printer className="w-3.5 h-3.5" /> Print / Save as PDF
        </button>
      </div>
      <div className="rounded-2xl border border-[#ebe4d3] bg-white shadow-sm p-6">
        <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">AI Profit Opportunity Report</div>
        <div className="mt-2 text-2xl font-bold">{rep.business}</div>
        <div className="text-sm text-slate-600">Period · {rep.period} days · Generated {printedAt}</div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
          <Kpi label="Revenue" value={fmt(rep.revenue)} />
          <Kpi label="Gross margin" value={`${rep.gross_margin_pct}%`} accent="emerald" />
          <Kpi label="Δ vs prior" value={rep.revenue_delta_pct === null ? "—" : `${rep.revenue_delta_pct > 0 ? "+" : ""}${rep.revenue_delta_pct}%`}
            accent={rep.revenue_delta_pct >= 0 ? "emerald" : "amber"} />
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          <ReportList title="Top opportunities"
            items={rep.opportunities.map((o) => `${o.title} · confidence ${(o.confidence * 100).toFixed(0)}%`)}
            emptyMsg="No pricing opportunities right now — all products within guardrails." />
          <ReportList title="Inventory risks"
            items={rep.risks.map((r) => `${r.title} · ${r.stock} in stock`)}
            emptyMsg="Stock levels healthy." />
        </div>

        <div className="mt-8">
          <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Top revenue this week</div>
          <div className="mt-3 space-y-1">
            {rep.top_products.map((p) => (
              <div key={p.sku} className="flex justify-between text-sm border-b border-[#f0e9d6] py-1.5">
                <div>{p.title}</div>
                <div className="font-mono">{fmt(p.revenue)} · {p.margin_pct}%</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 pt-4 border-t border-[#ebe4d3] text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
          Powered by WebsiteTech · AI Commerce Intelligence
        </div>
      </div>

      <div className="print-hide">
        <SlackPanel />
      </div>
    </div>
  );
}


function SlackPanel() {
  const [webhook, setWebhook] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState("");
  const [authNeeded, setAuthNeeded] = useState(false);

  useEffect(() => {
    commerceSlackWebhookGet()
      .then((d) => setWebhook(d.slack_webhook_url || ""))
      .catch((e) => { if (e?.response?.status === 401) setAuthNeeded(true); });
  }, []);

  const save = async () => {
    setBusy(true); setFlash("");
    try {
      const r = await commerceSlackWebhookPut(webhook.trim() || null);
      setWebhook(r.slack_webhook_url || "");
      setSavedFlash(true); setTimeout(() => setSavedFlash(false), 2500);
    } catch (e) {
      setFlash(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  const showPreview = async () => {
    setBusy(true); setFlash("");
    try { setPreview(await commerceSlackPreview()); }
    catch (e) { setFlash(e?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };
  const sendNow = async () => {
    setBusy(true); setFlash("");
    try {
      const r = await commerceSlackSend();
      setFlash(r.ok ? "✓ Delivered to Slack" : `Failed: ${r.error}`);
    } catch (e) {
      setFlash(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  if (authNeeded) {
    return (
      <div className="rounded-xl border border-fuchsia-400/25 bg-gradient-to-br from-fuchsia-500/[0.08] to-transparent p-6"
        data-testid="commerce-slack-panel">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-fuchsia-700 font-bold">
          <Slack className="w-3.5 h-3.5" /> Slack Block-Kit digest
        </div>
        <div className="mt-3 text-sm text-slate-700">
          Post the weekly digest as a rich Block-Kit card with clickable Review buttons on each opportunity.
        </div>
        <Link to="/login" className="mt-4 inline-flex items-center gap-1.5 bg-fuchsia-500 hover:bg-fuchsia-400 text-white rounded px-4 py-2 text-xs font-bold">
          Sign in to configure Slack <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-fuchsia-400/25 bg-gradient-to-br from-fuchsia-500/[0.08] to-transparent p-6"
      data-testid="commerce-slack-panel">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-fuchsia-700 font-bold">
        <Slack className="w-3.5 h-3.5" /> Slack Block-Kit digest
      </div>
      <div className="mt-2 text-sm text-slate-700">
        Post the weekly digest as a rich Block-Kit card with clickable Approve buttons on each opportunity.
      </div>
      <div className="mt-4 flex gap-2 flex-wrap">
        <input value={webhook} onChange={(e) => setWebhook(e.target.value)}
          data-testid="commerce-slack-webhook"
          placeholder="https://hooks.slack.com/services/..."
          className="flex-1 min-w-[280px] rounded bg-white border border-[#ebe4d3] focus:border-fuchsia-400 outline-none px-3 py-2.5 text-sm font-mono" />
        <button onClick={save} disabled={busy}
          data-testid="commerce-slack-save"
          className="inline-flex items-center gap-1.5 bg-fuchsia-500 hover:bg-fuchsia-400 disabled:opacity-50 text-white rounded px-4 py-2.5 text-sm font-bold transition">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Slack className="w-3.5 h-3.5" />}
          Save webhook {savedFlash && <CheckCircle2 className="w-3.5 h-3.5" />}
        </button>
      </div>
      <div className="mt-3 flex gap-2 flex-wrap">
        <button onClick={showPreview} disabled={busy}
          data-testid="commerce-slack-preview"
          className="inline-flex items-center gap-1.5 border border-fuchsia-400/40 hover:border-fuchsia-400 bg-fuchsia-500/10 hover:bg-fuchsia-500/20 text-fuchsia-200 rounded px-4 py-2 text-xs font-semibold">
          <MessageSquare className="w-3.5 h-3.5" /> Preview Block Kit
        </button>
        <button onClick={sendNow} disabled={busy || !webhook.trim()}
          data-testid="commerce-slack-send"
          className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-slate-900 rounded px-4 py-2 text-xs font-bold">
          <Send className="w-3.5 h-3.5" /> Send now
        </button>
      </div>
      {flash && <div className="mt-3 text-xs text-emerald-700" data-testid="commerce-slack-flash">{flash}</div>}
      {preview && (
        <div className="mt-4 rounded-lg bg-[#faf5e8] border border-[#ebe4d3] p-4"
          data-testid="commerce-slack-preview-json">
          <div className="text-xs uppercase tracking-widest text-fuchsia-700 font-bold mb-2">Block Kit payload · {preview.blocks.length} blocks</div>
          <pre className="text-[10px] font-mono text-slate-700 overflow-auto max-h-64 whitespace-pre-wrap">
{JSON.stringify(preview.blocks, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}


function ReportList({ title, items, emptyMsg }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-slate-600 font-bold">{title}</div>
      <ul className="mt-3 space-y-2">
        {items.length === 0 && <li className="text-sm text-slate-500">{emptyMsg}</li>}
        {items.map((i, idx) => (
          <li key={idx} className="text-sm text-slate-800 border-l-2 border-emerald-500/40 pl-3">{i}</li>
        ))}
      </ul>
    </div>
  );
}


// ============================================================================
// Inventory Health — categorised stock buckets
// ============================================================================
const INV_BUCKETS = [
  { key: "stockout_risk", label: "Stockout risk", cls: "border-rose-400/30 bg-rose-500/10 text-rose-200", icon: AlertTriangle },
  { key: "low_stock", label: "Low stock — reorder now", cls: "border-amber-400/30 bg-amber-500/10 text-amber-200", icon: Bell },
  { key: "overstock", label: "Overstock — mark down or bundle", cls: "border-indigo-400/30 bg-indigo-500/10 text-indigo-200", icon: Boxes },
  { key: "dead_stock", label: "Dead stock — clear now", cls: "border-fuchsia-400/30 bg-fuchsia-500/10 text-fuchsia-200", icon: Trash2 },
  { key: "aging", label: "Aging — no sales in 30d+", cls: "border-orange-400/30 bg-orange-500/10 text-orange-200", icon: Activity },
  { key: "healthy", label: "Healthy", cls: "border-emerald-400/30 bg-emerald-500/10 text-emerald-200", icon: CheckCircle2 },
];

function InventoryTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const d = await commerceInventoryHealth(30); setData(d); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  if (loading || !data) return <div className="text-slate-600 text-sm">Loading inventory health…</div>;
  return (
    <div className="space-y-6" data-testid="commerce-inventory-health">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {INV_BUCKETS.map(({ key, label, cls, icon: I }) => (
          <div key={key} className={`rounded-xl border p-4 ${cls}`}
            data-testid={`commerce-inv-tile-${key}`}>
            <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold">
              <I className="w-3 h-3" /> {label}
            </div>
            <div className="mt-2 text-3xl font-bold font-mono">{data.totals[key] || 0}</div>
          </div>
        ))}
      </div>
      {INV_BUCKETS.map(({ key, label }) => (
        (data.buckets[key] || []).length > 0 && (
          <div key={key} className="rounded-2xl border border-[#ebe4d3] bg-white shadow-sm p-5"
            data-testid={`commerce-inv-bucket-${key}`}>
            <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">{label}</div>
            <div className="mt-4 overflow-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-widest text-slate-600 font-bold">
                  <tr>
                    <th className="text-left py-2">Product</th>
                    <th className="text-right py-2">Stock</th>
                    <th className="text-right py-2">Velocity</th>
                    <th className="text-right py-2">Days of supply</th>
                    <th className="text-left py-2 pl-4">Recommended action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.buckets[key].map((r) => (
                    <tr key={r.sku} className="border-t border-[#f0e9d6]">
                      <td className="py-2"><div className="font-semibold">{r.title}</div>
                        <div className="text-[10px] text-slate-500 font-mono">{r.sku}</div></td>
                      <td className="py-2 text-right font-mono">{r.stock}</td>
                      <td className="py-2 text-right font-mono">{r.velocity_per_day}/d</td>
                      <td className="py-2 text-right font-mono">{r.days_of_supply ?? "—"}</td>
                      <td className="py-2 pl-4 text-slate-700">{r.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      ))}
    </div>
  );
}


// ============================================================================
// Team & Roles — Owner / Approver / Viewer
// ============================================================================
function TeamTab() {
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("approver");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [authNeeded, setAuthNeeded] = useState(false);
  const load = async () => {
    try {
      const d = await commerceTeamList();
      setRows(d.items || []); setAuthNeeded(false);
    } catch (e) {
      if (e?.response?.status === 401) setAuthNeeded(true);
      else setErr(e?.response?.data?.detail || "Failed to load team");
    }
  };
  useEffect(() => { load(); }, []);
  if (authNeeded) {
    return (
      <div className="max-w-2xl rounded-xl border border-amber-400/30 bg-amber-500/5 p-6 text-sm text-amber-100"
        data-testid="commerce-team-auth-needed">
        <ShieldCheck className="w-6 h-6 text-amber-700 mb-2" />
        <div className="font-bold text-base">Sign in to manage your team.</div>
        <p className="mt-2 text-amber-200/80">Roles + audit trail live inside your tenant — the demo view is anonymous.</p>
        <Link to="/login" className="mt-4 inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-slate-900 rounded px-4 py-2 text-xs font-bold">
          Sign in <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    );
  }
  const add = async () => {
    if (!email.includes("@")) { setErr("Enter a valid email"); return; }
    setBusy(true); setErr("");
    try {
      await commerceTeamUpsert({ email, role });
      setEmail(""); await load();
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const remove = async (e) => {
    if (!window.confirm(`Remove ${e} from team?`)) return;
    try { await commerceTeamRemove(e); await load(); }
    catch (err) { alert(err?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-5 max-w-3xl" data-testid="commerce-team">
      <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/5 p-4 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-slate-800">
          Roles: <strong className="text-emerald-700">Owner</strong> (full access, billing) ·{" "}
          <strong className="text-indigo-700">Approver</strong> (review + approve recs) ·{" "}
          <strong className="text-slate-700">Viewer</strong> (read-only dashboards).
          Extended per-module permissions ship in Phase 2.
        </div>
      </div>
      <div className="rounded-2xl border border-[#ebe4d3] bg-white shadow-sm p-5">
        <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Invite a teammate</div>
        <div className="mt-3 flex gap-2 flex-wrap">
          <input value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="name@company.com"
            data-testid="commerce-team-email"
            className="flex-1 min-w-[220px] rounded bg-white border border-[#ebe4d3] focus:border-emerald-400 outline-none px-3 py-2.5 text-sm" />
          <select value={role} onChange={(e) => setRole(e.target.value)}
            data-testid="commerce-team-role"
            className="rounded bg-white border border-[#ebe4d3] focus:border-emerald-400 outline-none px-3 py-2.5 text-sm">
            <option value="owner">Owner</option>
            <option value="approver">Approver</option>
            <option value="viewer">Viewer</option>
          </select>
          <button onClick={add} disabled={busy}
            data-testid="commerce-team-add"
            className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-slate-900 rounded px-4 py-2.5 text-sm font-bold">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />} Save
          </button>
        </div>
        {err && <div className="mt-2 text-xs text-rose-700">{err}</div>}
      </div>
      <div className="rounded-2xl border border-[#ebe4d3] bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#faf5e8] text-xs uppercase tracking-widest text-slate-600 font-bold">
            <tr>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.email} className="border-t border-[#f0e9d6]" data-testid={`commerce-team-row-${m.email}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold">{m.display_name || m.email}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{m.email}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs uppercase tracking-widest font-bold rounded px-2 py-0.5 border ${
                    m.role === "owner" ? "border-emerald-400/40 bg-emerald-500/15 text-emerald-700" :
                    m.role === "approver" ? "border-indigo-400/40 bg-indigo-500/15 text-indigo-700" :
                    "border-slate-400/30 bg-[#faf5e8]0/10 text-slate-700"
                  }`}>{m.role}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => remove(m.email)}
                    data-testid={`commerce-team-remove-${m.email}`}
                    className="inline-flex items-center gap-1 text-[11px] text-rose-700 hover:text-rose-200">
                    <Trash2 className="w-3 h-3" /> Remove
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={3} className="px-4 py-8 text-center text-slate-500">No teammates yet. You'll appear as owner after your first action.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ============================================================================
// Backtesting — realised vs projected profit impact of executed recs
// ============================================================================
function BacktestTab() {
  const [data, setData] = useState(null);
  useEffect(() => { commerceBacktest(90).then(setData); }, []);
  if (!data) return <div className="text-slate-600 text-sm">Loading backtest…</div>;
  return (
    <div className="space-y-5" data-testid="commerce-backtest">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Kpi label="Executed recs (90d)" value={data.count} />
        <Kpi label="Realised gross profit" value={`₹${Math.round(data.total_realised_gp).toLocaleString("en-IN")}`}
          accent="emerald" />
        <Kpi label="Projected (sum of 30d deltas)"
          value={`₹${Math.round(data.total_projected_gp_delta_30d).toLocaleString("en-IN")}`} />
      </div>
      <div className="rounded-2xl border border-[#ebe4d3] bg-white shadow-sm p-5">
        <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Rec-by-rec outcomes</div>
        <div className="mt-4 overflow-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-slate-600 font-bold">
              <tr>
                <th className="text-left py-2">Rec</th>
                <th className="text-left py-2">Kind</th>
                <th className="text-right py-2">Prior → New</th>
                <th className="text-right py-2">Realised units</th>
                <th className="text-right py-2">Realised GP</th>
                <th className="text-right py-2">Projected GP Δ (30d)</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.rec_id} className="border-t border-[#f0e9d6]"
                  data-testid={`commerce-backtest-row-${r.rec_id}`}>
                  <td className="py-2">
                    <div className="font-semibold">{r.title || r.sku}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{r.sku} · {r.executed_at?.slice(0, 10)}</div>
                    {r.small_sample && <div className="text-[10px] text-amber-700">Small sample — treat with caution</div>}
                  </td>
                  <td className="py-2 text-slate-700">{r.kind}</td>
                  <td className="py-2 text-right font-mono text-xs">
                    ₹{r.prior_price?.toFixed?.(2) || "—"} → ₹{r.new_price?.toFixed?.(2) || "—"}
                  </td>
                  <td className="py-2 text-right font-mono">{r.units}</td>
                  <td className={`py-2 text-right font-mono ${r.realised_gp >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                    ₹{Math.round(r.realised_gp).toLocaleString("en-IN")}
                  </td>
                  <td className="py-2 text-right font-mono text-slate-600">
                    ₹{Math.round(r.projected_gp_delta_30d).toLocaleString("en-IN")}
                  </td>
                </tr>
              ))}
              {data.items.length === 0 && (
                <tr><td colSpan={6} className="py-10 text-center text-slate-500">
                  No executed recommendations in the last 90 days — approve and execute a few to see backtested outcomes.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-3 text-[11px] text-slate-500">
          Realised profit compares actual sales in the 30 days after each execution to the pre-execution baseline. Sample sizes below 30 units are labelled.
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// Audit log
// ============================================================================
function AuditTab() {
  const [rows, setRows] = useState([]);
  const [authNeeded, setAuthNeeded] = useState(false);
  useEffect(() => {
    commerceAuditList({ limit: 200 }).then((d) => setRows(d.items || []))
      .catch((e) => { if (e?.response?.status === 401) setAuthNeeded(true); });
  }, []);
  if (authNeeded) {
    return (
      <div className="max-w-2xl rounded-xl border border-amber-400/30 bg-amber-500/5 p-6 text-sm text-amber-100"
        data-testid="commerce-audit-auth-needed">
        <ClipboardList className="w-6 h-6 text-amber-700 mb-2" />
        <div className="font-bold text-base">Sign in to view your audit log.</div>
        <Link to="/login" className="mt-4 inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-slate-900 rounded px-4 py-2 text-xs font-bold">
          Sign in <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="commerce-audit">
      <div className="text-xs text-slate-600">Append-only log of every action taken by any teammate. Immutable evidence for compliance + retros.</div>
      <div className="rounded-2xl border border-[#ebe4d3] bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#faf5e8] text-xs uppercase tracking-widest text-slate-600 font-bold">
            <tr>
              <th className="text-left px-4 py-3">When</th>
              <th className="text-left px-4 py-3">Actor</th>
              <th className="text-left px-4 py-3">Event</th>
              <th className="text-left px-4 py-3">SKU</th>
              <th className="text-left px-4 py-3">Detail</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <tr key={e.id} className="border-t border-[#f0e9d6]" data-testid={`commerce-audit-row-${e.id}`}>
                <td className="px-4 py-3 text-[11px] font-mono text-slate-600">{e.at?.replace("T", " ").slice(0, 19)}</td>
                <td className="px-4 py-3 text-slate-800">{e.actor}</td>
                <td className="px-4 py-3">
                  <span className="text-xs uppercase tracking-widest font-bold rounded px-2 py-0.5 border border-emerald-400/30 bg-emerald-500/10 text-emerald-700">{e.event}</span>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-slate-600">{e.sku || "—"}</td>
                <td className="px-4 py-3 text-xs text-slate-700">
                  {e.detail && Object.keys(e.detail).length > 0
                    ? <code className="font-mono text-[11px] text-slate-600">{JSON.stringify(e.detail).slice(0, 80)}</code>
                    : "—"}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">No audit events yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ============================================================================
// Forecasts — 30-day seasonality-aware demand forecast per SKU
// ============================================================================
function ForecastsTab() {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  useEffect(() => {
    commerceForecastSummary({ horizon_days: 30, top: 50 })
      .then((d) => { setData(d); setLoading(false); });
  }, []);
  const loadDetail = async (sku) => {
    setDetailLoading(true);
    try { setDetail(await commerceForecastSku(sku, { horizon_days: 30 })); }
    finally { setDetailLoading(false); }
  };
  if (loading || !data) return <div className="text-slate-600 text-sm">Building forecasts…</div>;
  return (
    <div className="space-y-5" data-testid="commerce-forecasts">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Kpi label="SKUs forecasted" value={data.count} />
        <Kpi label="Horizon" value={`${data.horizon_days} days`} accent="emerald" />
        <Kpi label="Stockout-risk SKUs"
          value={data.items.filter((r) => r.stockout_risk).length}
          accent="amber" />
      </div>
      <div className="rounded-2xl border border-[#ebe4d3] bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#faf5e8] text-xs uppercase tracking-widest text-slate-600 font-bold">
            <tr>
              <th className="text-left px-4 py-3">Product</th>
              <th className="text-right px-4 py-3">Stock</th>
              <th className="text-right px-4 py-3">30d forecast</th>
              <th className="text-right px-4 py-3">Range (low → high)</th>
              <th className="text-right px-4 py-3">Trend/day</th>
              <th className="text-right px-4 py-3">Days to stockout</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((r) => (
              <tr key={r.sku} className={`border-t border-[#f0e9d6] hover:bg-white/[0.02] ${r.stockout_risk ? "bg-amber-500/[0.05]" : ""}`}
                data-testid={`commerce-forecast-row-${r.sku}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold">{r.title}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{r.sku} · {r.category || "—"}</div>
                </td>
                <td className="px-4 py-3 text-right font-mono">{r.current_stock}</td>
                <td className="px-4 py-3 text-right font-mono font-bold text-emerald-700">{r.forecast_units_30d}</td>
                <td className="px-4 py-3 text-right font-mono text-xs text-slate-600">{r.low_30d} → {r.high_30d}</td>
                <td className={`px-4 py-3 text-right font-mono ${r.trend_per_day > 0 ? "text-emerald-700" : r.trend_per_day < 0 ? "text-rose-700" : "text-slate-600"}`}>
                  {r.trend_per_day > 0 ? "+" : ""}{r.trend_per_day}
                </td>
                <td className={`px-4 py-3 text-right font-mono ${r.stockout_risk ? "text-amber-700 font-bold" : "text-slate-700"}`}>
                  {r.days_to_stockout !== null ? `${r.days_to_stockout}d` : "—"}
                  {r.stockout_risk && <AlertTriangle className="inline w-3 h-3 ml-1" />}
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => loadDetail(r.sku)}
                    data-testid={`commerce-forecast-inspect-${r.sku}`}
                    className="text-[11px] font-bold text-emerald-700 hover:text-emerald-200">Inspect →</button>
                </td>
              </tr>
            ))}
            {data.items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">No SKUs to forecast yet — import products and orders first.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {(detailLoading || detail) && (
        <div className="rounded-xl border border-emerald-400/25 bg-emerald-500/[0.04] p-5"
          data-testid="commerce-forecast-detail">
          {detailLoading && !detail && <div className="text-slate-600 text-sm">Loading detail…</div>}
          {detail && (
            <>
              <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Forecast detail · {detail.sku}</div>
              <div className="mt-2 text-lg font-bold">{detail.title}</div>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                <MiniStat label="30d forecast" value={`${detail.totals.units}u`} accent="emerald" />
                <MiniStat label="Range" value={`${detail.totals.low}–${detail.totals.high}`} />
                <MiniStat label="Avg / day" value={detail.avg_per_day} />
                <MiniStat label="Trend / day" value={detail.trend_per_day}
                  accent={detail.trend_per_day > 0 ? "emerald" : detail.trend_per_day < 0 ? "rose" : "slate"} />
              </div>
              <div className="mt-4">
                <div className="text-xs uppercase tracking-widest text-slate-600 font-bold">Weekday seasonality (relative to average)</div>
                <div className="mt-2 flex gap-1">
                  {Object.entries(detail.seasonality).map(([wd, factor]) => (
                    <div key={wd} className="flex-1 rounded bg-[#faf5e8] border border-[#ebe4d3] p-2 text-center">
                      <div className="text-[10px] text-slate-600 font-bold">{wd}</div>
                      <div className={`text-sm font-mono font-bold ${factor >= 1.05 ? "text-emerald-700" : factor <= 0.85 ? "text-rose-700" : "text-slate-800"}`}>
                        {factor}×
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-4">
                <div className="text-xs uppercase tracking-widest text-slate-600 font-bold">Next 30 days (sparkline)</div>
                <div className="mt-2 flex items-end gap-0.5 h-16">
                  {detail.forecast.map((d, i) => {
                    const max = Math.max(...detail.forecast.map((f) => f.high_units), 1);
                    const height = (d.forecast_units / max) * 100;
                    return (
                      <div key={i} title={`${d.date}: ${d.forecast_units}u`}
                        className="flex-1 bg-emerald-500/60 hover:bg-emerald-400 rounded-sm min-h-[2px]"
                        style={{ height: `${Math.max(4, height)}%` }} />
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}


// ============================================================================
// Bundle Recommendations — co-purchase mining
// ============================================================================
function BundlesTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    commerceBundles({ lookback_days: 60, min_orders: 3, top: 15 })
      .then((d) => { setData(d); setLoading(false); });
  }, []);
  if (loading || !data) return <div className="text-slate-600 text-sm">Mining co-purchase patterns…</div>;
  return (
    <div className="space-y-5" data-testid="commerce-bundles">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Kpi label="Orders analyzed" value={data.total_orders} />
        <Kpi label="Bundle candidates" value={data.count} accent="emerald" />
        <Kpi label="Lookback" value={`${data.period_days}d`} />
      </div>
      {data.candidates.length === 0 && (
        <div className="rounded-2xl border border-[#ebe4d3] bg-white shadow-sm p-8 text-center text-slate-600">
          No multi-item orders in the last {data.period_days} days yet. Bundle mining needs a few orders with 2+ SKUs each.
        </div>
      )}
      <div className="space-y-3">
        {data.candidates.map((b) => (
          <div key={`${b.sku_a}-${b.sku_b}`}
            data-testid={`commerce-bundle-${b.sku_a}-${b.sku_b}`}
            className="rounded-2xl border border-[#ebe4d3] bg-white shadow-sm p-5">
            <div className="flex flex-wrap items-center gap-3">
              <div className="text-[10px] uppercase tracking-widest font-bold rounded px-2 py-1 border border-indigo-400/30 bg-indigo-500/15 text-indigo-700">
                bundle · lift {b.lift}×
              </div>
              <div className="text-xs uppercase tracking-widest text-slate-600 font-bold">
                confidence {(b.confidence * 100).toFixed(0)}%
              </div>
              <div className="ml-auto text-xs text-slate-600 font-mono">bought together {b.orders_together}×</div>
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="rounded-lg bg-[#faf5e8] border border-[#ebe4d3] p-3">
                <div className="text-[10px] text-slate-500 font-mono">{b.sku_a}</div>
                <div className="text-sm font-semibold">{b.title_a}</div>
              </div>
              <div className="rounded-lg bg-[#faf5e8] border border-[#ebe4d3] p-3">
                <div className="text-[10px] text-slate-500 font-mono">{b.sku_b}</div>
                <div className="text-sm font-semibold">{b.title_b}</div>
              </div>
            </div>
            <div className="mt-3 text-sm text-slate-700">{b.why}</div>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
              <MiniStat label="Combined price" value={`₹${b.combined_price.toFixed(2)}`} />
              <MiniStat label={`Bundle @ -${b.suggested_discount_pct}%`}
                value={`₹${b.bundle_price.toFixed(2)}`} accent="emerald" />
              <MiniStat label="Margin" value={b.combined_margin_pct !== null ? `${b.combined_margin_pct}%` : "—"}
                accent={b.combined_margin_pct >= 25 ? "emerald" : "amber"} />
              <MiniStat label="Est. GP 30d"
                value={b.projected_gp_30d !== null ? `₹${Math.round(b.projected_gp_30d).toLocaleString("en-IN")}` : "—"}
                accent="emerald" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ============================================================================
// Region-level competitor pricing gaps
// ============================================================================
function RegionsTab() {
  const [data, setData] = useState(null);
  const [region, setRegion] = useState("");
  useEffect(() => { commerceCompetitorRegions(24 * 30).then(setData); }, []);
  if (!data) return <div className="text-slate-600 text-sm">Loading regional intel…</div>;
  const rows = region ? data.items.filter((r) => r.region === region) : data.items;
  return (
    <div className="space-y-5" data-testid="commerce-regions">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Kpi label="Regions tracked" value={data.regions.length} />
        <Kpi label="SKU × region rows" value={data.count} accent="emerald" />
        <Kpi label="We're more expensive"
          value={data.items.filter((r) => (r.gap_vs_min_pct || 0) > 0).length}
          accent="amber" />
        <Kpi label="Biggest gap"
          value={data.items.length ? `${Math.max(...data.items.map((r) => r.gap_vs_min_pct || 0))}%` : "—"}
          accent="rose" />
      </div>
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setRegion("")}
          data-testid="commerce-region-all"
          className={`text-xs uppercase tracking-widest font-bold px-3 py-1.5 rounded ${region === "" ? "bg-emerald-500 text-slate-900" : "bg-[#faf5e8] text-slate-700 border border-[#ebe4d3]"}`}>
          All regions
        </button>
        {data.regions.map((r) => (
          <button key={r} onClick={() => setRegion(r)}
            data-testid={`commerce-region-${r}`}
            className={`text-xs uppercase tracking-widest font-bold px-3 py-1.5 rounded ${region === r ? "bg-emerald-500 text-slate-900" : "bg-[#faf5e8] text-slate-700 border border-[#ebe4d3]"}`}>
            <MapPin className="w-3 h-3 inline mr-1" /> {r}
          </button>
        ))}
      </div>
      <div className="rounded-2xl border border-[#ebe4d3] bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#faf5e8] text-xs uppercase tracking-widest text-slate-600 font-bold">
            <tr>
              <th className="text-left px-4 py-3">Product · Region</th>
              <th className="text-right px-4 py-3">Our price</th>
              <th className="text-right px-4 py-3">Cheapest competitor</th>
              <th className="text-right px-4 py-3">Range (min–max)</th>
              <th className="text-right px-4 py-3">Gap vs cheapest</th>
              <th className="text-left px-4 py-3">Promotions seen</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.sku}-${r.region}`} className={`border-t border-[#f0e9d6] ${(r.gap_vs_min_pct || 0) > 5 ? "bg-amber-500/[0.04]" : ""}`}
                data-testid={`commerce-region-row-${r.sku}-${r.region}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold">{r.our_title}</div>
                  <div className="text-[10px] text-slate-500"><MapPin className="w-3 h-3 inline mr-1" />{r.region} · {r.competitor_count} competitors</div>
                </td>
                <td className="px-4 py-3 text-right font-mono font-bold text-emerald-700">
                  {r.our_price !== null ? `₹${r.our_price.toFixed(2)}` : "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="font-mono">₹{r.min_competitor_price.toFixed(2)}</div>
                  <div className="text-[10px] text-slate-500">{r.cheapest_competitor}</div>
                </td>
                <td className="px-4 py-3 text-right font-mono text-xs text-slate-600">
                  ₹{r.min_competitor_price.toFixed(2)} – ₹{r.max_competitor_price.toFixed(2)}
                </td>
                <td className={`px-4 py-3 text-right font-mono font-bold ${(r.gap_vs_min_pct || 0) > 5 ? "text-amber-700" : (r.gap_vs_min_pct || 0) < 0 ? "text-emerald-700" : "text-slate-700"}`}>
                  {r.gap_vs_min_pct !== null ? `${r.gap_vs_min_pct > 0 ? "+" : ""}${r.gap_vs_min_pct}%` : "—"}
                </td>
                <td className="px-4 py-3 text-[11px] text-slate-600">
                  {r.active_promotions.length ? r.active_promotions.join(" · ") : "—"}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                Add competitors with a region or postal_code to unlock regional analysis.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ============================================================================
// Ad Spend — Google + Meta ads plugged into product margins
// ============================================================================
function AdSpendTab() {
  const [data, setData] = useState(null);
  const [csv, setCsv] = useState("");
  const [source, setSource] = useState("google_ads");
  const [flash, setFlash] = useState("");
  const [busy, setBusy] = useState(false);
  const [authNeeded, setAuthNeeded] = useState(false);
  const load = () => commerceAdsSummary(30).then(setData);
  useEffect(() => { load(); }, []);
  const importCsv = async () => {
    if (!csv.trim()) return;
    setBusy(true); setFlash("");
    try {
      const r = await commerceAdsImportCsv(csv, source);
      setFlash(`✓ Imported ${r.inserted} new + ${r.updated} updated rows`);
      setCsv("");
      await load();
    } catch (e) {
      if (e?.response?.status === 401) setAuthNeeded(true);
      else setFlash(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };
  if (!data) return <div className="text-slate-600 text-sm">Loading ad-cost analysis…</div>;
  const t = data.totals;
  return (
    <div className="space-y-5" data-testid="commerce-ads">
      <AdsIntegrationsPanel />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Revenue (30d)" value={`₹${Math.round(t.revenue).toLocaleString("en-IN")}`} />
        <Kpi label="Gross profit" value={`₹${Math.round(t.gross_profit).toLocaleString("en-IN")}`}
          accent="emerald" />
        <Kpi label="Ad spend" value={`₹${Math.round(t.ad_spend).toLocaleString("en-IN")}`}
          accent="amber" />
        <Kpi label="Contribution after ads"
          value={`₹${Math.round(t.contribution_after_ads).toLocaleString("en-IN")}`}
          accent={t.contribution_after_ads >= 0 ? "emerald" : "rose"} />
        <Kpi label="Blended ACoS" value={`${t.blended_acos_pct}%`}
          accent={t.blended_acos_pct >= 20 ? "amber" : "slate"} />
      </div>

      <div className="rounded-2xl border border-[#ebe4d3] bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#faf5e8] text-xs uppercase tracking-widest text-slate-600 font-bold">
            <tr>
              <th className="text-left px-4 py-3">Product</th>
              <th className="text-right px-4 py-3">Revenue</th>
              <th className="text-right px-4 py-3">Gross profit</th>
              <th className="text-right px-4 py-3">Ad spend</th>
              <th className="text-right px-4 py-3">ACoS</th>
              <th className="text-right px-4 py-3">Contribution after ads</th>
              <th className="text-right px-4 py-3">CM %</th>
            </tr>
          </thead>
          <tbody>
            {data.items.filter((r) => r.revenue > 0 || r.ad_spend > 0).map((r) => (
              <tr key={r.sku} className={`border-t border-[#f0e9d6] ${r.unprofitable_after_ads ? "bg-rose-500/[0.06]" : ""}`}
                data-testid={`commerce-ads-row-${r.sku}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold flex items-center gap-2">
                    {r.title}
                    {r.unprofitable_after_ads && (
                      <span className="text-[10px] uppercase tracking-widest font-bold rounded px-1.5 py-0.5 border border-rose-400/40 bg-rose-500/15 text-rose-700"
                        data-testid={`commerce-ads-unprofitable-${r.sku}`}>Unprofitable after ads</span>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-500 font-mono">
                    {r.sku} · {Object.entries(r.ad_spend_by_source).map(([k, v]) => `${k}: ₹${Math.round(v).toLocaleString("en-IN")}`).join(" · ") || "—"}
                  </div>
                </td>
                <td className="px-4 py-3 text-right font-mono">₹{Math.round(r.revenue).toLocaleString("en-IN")}</td>
                <td className="px-4 py-3 text-right font-mono text-emerald-700">₹{Math.round(r.gross_profit).toLocaleString("en-IN")}</td>
                <td className="px-4 py-3 text-right font-mono text-amber-700">₹{Math.round(r.ad_spend).toLocaleString("en-IN")}</td>
                <td className={`px-4 py-3 text-right font-mono ${r.acos_pct >= 50 ? "text-rose-700 font-bold" : r.acos_pct >= 30 ? "text-amber-700" : "text-slate-700"}`}>
                  {r.acos_pct !== null ? `${r.acos_pct}%` : "—"}
                </td>
                <td className={`px-4 py-3 text-right font-mono font-bold ${r.contribution_after_ads < 0 ? "text-rose-700" : "text-slate-800"}`}>
                  ₹{Math.round(r.contribution_after_ads).toLocaleString("en-IN")}
                </td>
                <td className={`px-4 py-3 text-right font-mono ${r.contribution_margin_pct < 0 ? "text-rose-700" : r.contribution_margin_pct >= 20 ? "text-emerald-700" : "text-slate-700"}`}>
                  {r.contribution_margin_pct}%
                </td>
              </tr>
            ))}
            {data.items.filter((r) => r.revenue > 0 || r.ad_spend > 0).length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                No ad spend or sales data yet — import a CSV below to see ad-cost-aware margins.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Import panel */}
      <div className="rounded-xl border border-amber-400/25 bg-gradient-to-br from-amber-500/[0.06] to-transparent p-5"
        data-testid="commerce-ads-import">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-amber-700 font-bold">
          <Upload className="w-3.5 h-3.5" /> Import ad spend (Google Ads / Meta Ads)
        </div>
        <div className="mt-1 text-xs text-slate-600">
          Header row required: <code className="font-mono">date,sku,source,campaign,spend,clicks,impressions,conversions</code>. Source column optional — falls back to the picker below.
        </div>
        <div className="mt-3 flex gap-2 items-center flex-wrap">
          <select value={source} onChange={(e) => setSource(e.target.value)}
            data-testid="commerce-ads-source"
            className="rounded bg-white border border-[#ebe4d3] focus:border-amber-400 outline-none px-3 py-2 text-sm">
            <option value="google_ads">Google Ads</option>
            <option value="meta_ads">Meta Ads (Facebook / Instagram)</option>
            <option value="other">Other</option>
          </select>
          <button onClick={() => setCsv("date,sku,source,campaign,spend,clicks,impressions,conversions\n2026-07-10,SW-KAJU-500,google_ads,brand_terms,240.50,45,2100,3\n2026-07-10,SW-GULAB-1KG,meta_ads,retargeting,180.00,32,1800,2\n")}
            data-testid="commerce-ads-sample"
            className="text-[11px] font-bold border border-[#dcd3bc] hover:border-[#c8bd9c] bg-[#faf5e8] hover:bg-[#f5efdd] rounded px-3 py-2">
            Insert sample CSV
          </button>
          {authNeeded && <span className="text-xs text-rose-700">Sign in to import</span>}
        </div>
        <textarea value={csv} onChange={(e) => setCsv(e.target.value)}
          data-testid="commerce-ads-csv"
          rows={5} placeholder="date,sku,source,campaign,spend,clicks,impressions,conversions\n2026-07-10,SW-KAJU-500,google_ads,brand_terms,240.50,45,2100,3"
          className="mt-3 w-full rounded bg-white border border-[#ebe4d3] focus:border-amber-400 outline-none px-3 py-2 text-xs font-mono" />
        <div className="mt-2 flex gap-2 items-center">
          <button onClick={importCsv} disabled={busy || !csv.trim()}
            data-testid="commerce-ads-upload"
            className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-slate-900 rounded px-4 py-2 text-sm font-bold">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Import spend
          </button>
          {flash && <span className="text-xs text-emerald-700" data-testid="commerce-ads-flash">{flash}</span>}
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// Image-Match Competitor Products — upload a photo, get catalog candidates
// ============================================================================
function ImageMatchPanel({ onMatched }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [hint, setHint] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [authNeeded, setAuthNeeded] = useState(false);
  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };
  const run = async () => {
    if (!preview) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      // Strip the data:image prefix so we only send raw base64
      const b64 = preview.split(",")[1] || preview;
      const r = await commerceImageMatch(b64, hint || null);
      setResult(r);
      if (onMatched) onMatched();
    } catch (e) {
      if (e?.response?.status === 401) setAuthNeeded(true);
      else setErr(e?.response?.data?.detail || "Vision match failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-purple-400/25 bg-gradient-to-br from-purple-500/[0.06] to-transparent p-5"
      data-testid="commerce-image-match">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-purple-700 font-bold">
        <Camera className="w-3.5 h-3.5" /> Image-match a competitor product
      </div>
      <div className="mt-1 text-xs text-slate-600">
        Upload a photo, Gemini Nano Banana extracts product attributes, then we rank your catalog by similarity. You still verify the match visually.
      </div>
      {authNeeded ? (
        <Link to="/login" className="mt-3 inline-flex items-center gap-1.5 bg-purple-500 hover:bg-purple-400 text-white rounded px-4 py-2 text-xs font-bold">
          Sign in to use image match <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      ) : (
        <>
          <div className="mt-3 flex gap-3 items-start flex-wrap">
            <label className="cursor-pointer inline-flex items-center gap-1.5 border border-purple-400/40 hover:border-purple-400 bg-purple-500/10 hover:bg-purple-500/20 text-purple-200 rounded px-3 py-2 text-xs font-semibold"
              data-testid="commerce-image-file-label">
              <Upload className="w-3.5 h-3.5" /> Pick photo
              <input type="file" accept="image/*" onChange={pickFile} className="hidden"
                data-testid="commerce-image-file" />
            </label>
            <input value={hint} onChange={(e) => setHint(e.target.value)}
              placeholder="Optional hint — e.g. 'brand: Haldiram, 500g pack'"
              data-testid="commerce-image-hint"
              className="flex-1 min-w-[220px] rounded bg-white border border-[#ebe4d3] focus:border-purple-400 outline-none px-3 py-2 text-sm" />
            <button onClick={run} disabled={busy || !preview}
              data-testid="commerce-image-run"
              className="inline-flex items-center gap-1.5 bg-purple-500 hover:bg-purple-400 disabled:opacity-40 text-white rounded px-4 py-2 text-xs font-bold">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Analyse
            </button>
          </div>
          {err && <div className="mt-2 text-xs text-rose-700">{err}</div>}
          {preview && (
            <div className="mt-4 flex gap-4 flex-wrap">
              <img src={preview} alt="preview" className="max-h-40 rounded border border-[#ebe4d3]"
                data-testid="commerce-image-preview" />
              {result && (
                <div className="flex-1 min-w-[280px] space-y-2">
                  <div className="text-xs uppercase tracking-widest text-purple-700 font-bold">Extracted attributes</div>
                  <div className="rounded bg-[#faf5e8] border border-[#ebe4d3] p-3 text-xs font-mono text-slate-700 max-h-40 overflow-auto">
                    <pre>{JSON.stringify(result.attributes, null, 2)}</pre>
                  </div>
                  <div className="text-xs uppercase tracking-widest text-purple-700 font-bold pt-1">Top matches ({result.candidates.length}/{result.total_ranked})</div>
                  <div className="space-y-1">
                    {result.candidates.map((c) => (
                      <div key={c.sku} className="flex items-center justify-between rounded border border-[#ebe4d3] bg-[#faf5e8] px-3 py-2 text-xs"
                        data-testid={`commerce-image-match-${c.sku}`}>
                        <div>
                          <div className="font-semibold">{c.title}</div>
                          <div className="text-[10px] text-slate-500 font-mono">{c.sku} · ₹{c.price?.toFixed?.(2) || "—"}</div>
                        </div>
                        <div className={`text-xs uppercase tracking-widest font-bold rounded px-2 py-0.5 border ${
                          c.match_confidence >= 0.4 ? "border-emerald-400/40 bg-emerald-500/15 text-emerald-700" :
                          c.match_confidence >= 0.2 ? "border-amber-400/40 bg-amber-500/15 text-amber-700" :
                          "border-slate-400/30 bg-[#faf5e8]0/10 text-slate-700"
                        }`}>{(c.match_confidence * 100).toFixed(0)}% match</div>
                      </div>
                    ))}
                  </div>
                  <div className="text-[10px] text-slate-500">{result.reminder}</div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ============================================================================
// Ads Integrations — Google Ads + Meta Ads OAuth connectors (hourly sync)
// ============================================================================
function AdsIntegrationsPanel() {
  const [rows, setRows] = useState([]);
  const [busySrc, setBusySrc] = useState("");
  const [msg, setMsg] = useState("");
  const [authNeeded, setAuthNeeded] = useState(false);
  const load = async () => {
    try { const d = await commerceAdsIntegrations(); setRows(d.items || []); setAuthNeeded(false); }
    catch (e) { if (e?.response?.status === 401) setAuthNeeded(true); }
  };
  useEffect(() => { load(); }, []);
  const connect = async (source) => {
    setBusySrc(source); setMsg("");
    try {
      // First try the real OAuth authorize flow — if backend has developer keys
      const auth = await commerceAdsOAuthAuthorize(source);
      if (auth?.configured && auth?.authorize_url) {
        window.open(auth.authorize_url, "_blank", "width=600,height=780");
        setMsg(`✓ Opened ${source.replace("_", " ")} consent window in a new tab — return here once you've granted access.`);
        await load();
        return;
      }
      // Fallback — placeholder mode (no developer keys yet)
      await commerceAdsIntegrationConnect({ source });
      setMsg(auth?.reason
        ? `${source.replace("_", " ")} connector saved in mocked mode. ${auth.reason}`
        : `✓ ${source.replace("_", " ")} connector ready (placeholder mode).`);
      await load();
    } catch (e) {
      if (e?.response?.status === 401) setAuthNeeded(true);
      else setMsg(e?.response?.data?.detail || "Failed");
    } finally { setBusySrc(""); }
  };
  const syncNow = async (source) => {
    setBusySrc(source); setMsg("");
    try {
      const r = await commerceAdsIntegrationSyncNow(source);
      setMsg(`✓ ${source.replace("_", " ")} pulled ${r.rows} rows (status: ${r.status})`);
      await load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Sync failed"); }
    finally { setBusySrc(""); }
  };
  if (authNeeded) return (
    <div className="rounded-xl border border-amber-400/25 bg-amber-500/[0.06] p-5 flex items-center justify-between gap-3">
      <div className="text-sm text-slate-800">Sign in to connect Google Ads / Meta Ads for hourly auto-sync.</div>
      <Link to="/login" className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-slate-900 rounded px-4 py-2 text-xs font-bold">
        Sign in <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
  const byKey = Object.fromEntries(rows.map((r) => [r.source, r]));
  const src = (key, label) => {
    const r = byKey[key];
    const isBusy = busySrc === key;
    return (
      <div key={key} className="rounded-lg border border-[#ebe4d3] bg-white p-4"
        data-testid={`commerce-ads-integration-${key}`}>
        <div className="flex items-center justify-between">
          <div className="text-sm font-bold">{label}</div>
          <span className={`text-[10px] uppercase tracking-widest font-bold rounded px-2 py-0.5 border ${
            !r ? "border-slate-400/30 bg-[#faf5e8]0/10 text-slate-700" :
            r.has_credentials ? "border-emerald-400/40 bg-emerald-500/15 text-emerald-700" :
            "border-amber-400/40 bg-amber-500/15 text-amber-700"
          }`}>
            {!r ? "not connected" : r.has_credentials ? "live" : "mocked"}
          </span>
        </div>
        <div className="mt-1 text-[11px] text-slate-600">
          {r ? `Last sync: ${r.last_sync_at?.slice(0, 19).replace("T", " ") || "never"} · rows ${r.last_sync_rows ?? 0}` : "Hourly auto-sync when connected."}
        </div>
        <div className="mt-3 flex gap-2">
          {!r && (
            <button onClick={() => connect(key)} disabled={isBusy}
              data-testid={`commerce-ads-connect-${key}`}
              className="flex-1 inline-flex items-center justify-center gap-1.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-slate-900 rounded px-3 py-2 text-xs font-bold">
              {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Power className="w-3.5 h-3.5" />} Connect
            </button>
          )}
          {r && (
            <button onClick={() => syncNow(key)} disabled={isBusy}
              data-testid={`commerce-ads-sync-${key}`}
              className="flex-1 inline-flex items-center justify-center gap-1.5 border border-[#dcd3bc] hover:border-[#c8bd9c] bg-[#faf5e8] hover:bg-[#f5efdd] rounded px-3 py-2 text-xs font-semibold">
              {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Sync now
            </button>
          )}
        </div>
      </div>
    );
  };
  return (
    <div className="rounded-xl border border-amber-400/25 bg-gradient-to-br from-amber-500/[0.04] to-transparent p-5"
      data-testid="commerce-ads-integrations">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-amber-700 font-bold">
        <RefreshCw className="w-3.5 h-3.5" /> Ad spend auto-sync
      </div>
      <div className="mt-1 text-xs text-slate-600">
        Hourly sync from Google Ads + Meta Ads — no more CSV imports once OAuth credentials are added. Placeholder mode still exercises the pipeline.
      </div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
        {src("google_ads", "Google Ads")}
        {src("meta_ads", "Meta Ads (Facebook / Instagram)")}
      </div>
      {msg && <div className="mt-3 text-xs text-emerald-700" data-testid="commerce-ads-integrations-msg">{msg}</div>}
    </div>
  );
}


// ============================================================================
// Autonomous Publishing — toggle + status card
// ============================================================================
function AutonomousPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [authNeeded, setAuthNeeded] = useState(false);
  const load = async () => {
    try { setData(await commerceAutonomousGet()); setAuthNeeded(false); }
    catch (e) { if (e?.response?.status === 401) setAuthNeeded(true); }
  };
  useEffect(() => { load(); }, []);
  const toggle = async () => {
    if (!data) return;
    if (!data.autonomous_publish_enabled) {
      if (!window.confirm("Turning autonomous publishing ON will push approved prices directly to Shopify + WooCommerce whenever a rec is approved. Every push is still guardrail-gated and audited. Continue?")) return;
    }
    setBusy(true);
    try { await commerceAutonomousSet(!data.autonomous_publish_enabled); await load(); }
    finally { setBusy(false); }
  };
  if (authNeeded) {
    return (
      <div className="rounded-xl border border-fuchsia-400/25 bg-gradient-to-br from-fuchsia-500/[0.08] to-transparent p-5 flex items-center justify-between gap-3"
        data-testid="commerce-autonomous-panel">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-fuchsia-700 font-bold">
            <Rocket className="w-3.5 h-3.5" /> Autonomous publishing
          </div>
          <div className="mt-1 text-sm text-slate-700">
            Once you're signed in, you can push approved prices straight to Shopify + WooCommerce.
          </div>
        </div>
        <Link to="/login" className="inline-flex items-center gap-1.5 bg-fuchsia-500 hover:bg-fuchsia-400 text-white rounded px-4 py-2 text-xs font-bold">
          Sign in <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    );
  }
  if (!data) return null;
  return (
    <div className="rounded-xl border border-fuchsia-400/25 bg-gradient-to-br from-fuchsia-500/[0.08] to-transparent p-5"
      data-testid="commerce-autonomous-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-fuchsia-700 font-bold">
            <Rocket className="w-3.5 h-3.5" /> Autonomous publishing
          </div>
          <div className="mt-1 text-sm text-slate-700">
            Push approved prices straight to your connected stores. Every publish is guardrail-gated and audited.
          </div>
          <div className="mt-2 flex gap-2 flex-wrap text-xs uppercase tracking-widest font-bold">
            <span className={`rounded px-2 py-0.5 border ${data.shopify_ready ? "border-emerald-400/40 bg-emerald-500/15 text-emerald-700" : "border-slate-400/30 bg-[#faf5e8]0/10 text-slate-700"}`}
              data-testid="commerce-autonomous-shopify-status">
              Shopify · {data.shopify_ready ? "connected" : "not connected"}
            </span>
            <span className={`rounded px-2 py-0.5 border ${data.woocommerce_ready ? "border-emerald-400/40 bg-emerald-500/15 text-emerald-700" : "border-slate-400/30 bg-[#faf5e8]0/10 text-slate-700"}`}
              data-testid="commerce-autonomous-woo-status">
              WooCommerce · {data.woocommerce_ready ? "connected" : "not connected"}
            </span>
          </div>
        </div>
        <button onClick={toggle} disabled={busy}
          data-testid="commerce-autonomous-toggle"
          className={`inline-flex items-center gap-2 rounded px-4 py-2.5 text-sm font-bold transition ${
            data.autonomous_publish_enabled
              ? "bg-emerald-500 hover:bg-emerald-400 text-slate-900"
              : "bg-fuchsia-500 hover:bg-fuchsia-400 text-white"
          } disabled:opacity-50`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
          {data.autonomous_publish_enabled ? "Enabled — click to disable" : "Enable autonomous"}
        </button>
      </div>
    </div>
  );
}


// ============================================================================
// Prompt Library — curated + tenant-owned prompt templates for the AI Ask box
// ============================================================================
const PROMPT_CATS = [
  { key: "all", label: "All" },
  { key: "pricing", label: "Pricing", color: "emerald" },
  { key: "promotion", label: "Promotion", color: "fuchsia" },
  { key: "inventory", label: "Inventory", color: "indigo" },
  { key: "customer", label: "Customer", color: "amber" },
  { key: "reporting", label: "Reporting", color: "sky" },
  { key: "other", label: "Other", color: "slate" },
];

function PromptLibraryTab({ onRun }) {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("all");
  const [copiedId, setCopiedId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", category: "pricing", template: "", tags: "" });
  const [authNeeded, setAuthNeeded] = useState(false);
  const load = () => commercePromptsList().then((d) => setItems(d.items || []));
  useEffect(() => { load(); }, []);
  const filtered = cat === "all" ? items : items.filter((p) => p.category === cat);
  const copy = (p) => {
    navigator.clipboard?.writeText(p.template);
    setCopiedId(p.id); setTimeout(() => setCopiedId(null), 1600);
  };
  const save = async () => {
    if (!form.title.trim() || !form.template.trim()) return;
    try {
      await commercePromptsAdd({
        title: form.title.trim(),
        category: form.category,
        template: form.template.trim(),
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
      });
      setForm({ title: "", category: "pricing", template: "", tags: "" });
      setCreating(false);
      await load();
    } catch (e) {
      if (e?.response?.status === 401) setAuthNeeded(true);
      else alert(e?.response?.data?.detail || "Save failed");
    }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await commercePromptsDelete(p.id); await load(); }
    catch (e) { alert(e?.response?.data?.detail || "Delete failed"); }
  };
  const catColor = (k) => (PROMPT_CATS.find((c) => c.key === k)?.color) || "slate";
  return (
    <div className="space-y-5" data-testid="commerce-prompts">
      <div className="rounded-xl border border-sky-400/25 bg-gradient-to-br from-sky-500/[0.06] to-transparent p-4 flex items-start gap-3">
        <BookOpen className="w-5 h-5 text-sky-700 flex-shrink-0 mt-0.5" />
        <div className="flex-1 text-sm text-slate-800">
          Ready-to-run business questions your team can ask the Commerce AI. Tap <span className="font-semibold text-emerald-700">Run</span> to send it straight to the Ask AI box, tap Copy to paste it elsewhere, or save your own for reuse across the tenant.
        </div>
        <button onClick={() => setCreating(!creating)}
          data-testid="commerce-prompts-new-toggle"
          className="inline-flex items-center gap-1.5 bg-sky-500 hover:bg-sky-400 text-slate-900 rounded px-4 py-2 text-xs font-bold">
          <Plus className="w-3.5 h-3.5" /> {creating ? "Cancel" : "New prompt"}
        </button>
      </div>
      {creating && !authNeeded && (
        <div className="rounded-2xl border border-[#ebe4d3] bg-white shadow-sm p-5 space-y-3"
          data-testid="commerce-prompts-form">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="commerce-prompts-title"
            placeholder="Prompt title"
            className="w-full rounded bg-white border border-[#ebe4d3] focus:border-sky-400 outline-none px-3 py-2.5 text-sm" />
          <div className="flex gap-2 flex-wrap">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="commerce-prompts-category"
              className="rounded bg-white border border-[#ebe4d3] focus:border-sky-400 outline-none px-3 py-2 text-sm">
              {PROMPT_CATS.filter((c) => c.key !== "all").map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
            <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })}
              placeholder="tags (comma-sep)"
              data-testid="commerce-prompts-tags"
              className="flex-1 min-w-[200px] rounded bg-white border border-[#ebe4d3] focus:border-sky-400 outline-none px-3 py-2 text-sm" />
          </div>
          <textarea value={form.template} onChange={(e) => setForm({ ...form, template: e.target.value })}
            rows={5} placeholder="Prompt template — this is what gets sent to the AI"
            data-testid="commerce-prompts-template"
            className="w-full rounded bg-white border border-[#ebe4d3] focus:border-sky-400 outline-none px-3 py-2 text-sm" />
          <button onClick={save}
            data-testid="commerce-prompts-save"
            className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-slate-900 rounded px-4 py-2 text-sm font-bold">
            <CheckCircle2 className="w-3.5 h-3.5" /> Save prompt
          </button>
        </div>
      )}
      {creating && authNeeded && (
        <div className="rounded-xl border border-amber-400/25 bg-amber-500/[0.05] p-5 text-sm text-amber-100">
          Sign in to save prompts to your tenant.
          <Link to="/login" className="ml-3 inline-flex items-center gap-1 bg-amber-500 hover:bg-amber-400 text-slate-900 rounded px-3 py-1.5 text-xs font-bold">Sign in <ArrowRight className="w-3 h-3" /></Link>
        </div>
      )}
      <div className="flex gap-2 flex-wrap">
        {PROMPT_CATS.map((c) => (
          <button key={c.key} onClick={() => setCat(c.key)}
            data-testid={`commerce-prompts-cat-${c.key}`}
            className={`text-xs uppercase tracking-widest font-bold px-3 py-1.5 rounded ${cat === c.key ? "bg-sky-500 text-slate-900" : "bg-[#faf5e8] text-slate-700 border border-[#ebe4d3]"}`}>
            {c.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filtered.map((p) => {
          const color = catColor(p.category);
          return (
            <div key={p.id}
              data-testid={`commerce-prompts-row-${p.id}`}
              className="rounded-2xl border border-[#ebe4d3] bg-white shadow-sm p-4 flex flex-col">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] uppercase tracking-widest font-bold rounded px-2 py-0.5 border border-${color}-400/40 bg-${color}-500/15 text-${color}-300`}>
                  {p.category}
                </span>
                {p.system && <span className="text-[10px] uppercase tracking-widest font-bold rounded px-2 py-0.5 border border-slate-400/30 bg-[#faf5e8]0/10 text-slate-600">built-in</span>}
                {(p.tags || []).slice(0, 3).map((t) => (
                  <span key={t} className="text-[10px] uppercase tracking-widest font-bold text-slate-500 font-mono">#{t}</span>
                ))}
              </div>
              <div className="mt-2 text-sm font-bold">{p.title}</div>
              <div className="mt-2 text-xs text-slate-600 leading-relaxed line-clamp-4">
                {p.template}
              </div>
              <div className="mt-3 flex gap-2 flex-wrap">
                {onRun && (
                  <button onClick={() => onRun(p.template)}
                    data-testid={`commerce-prompts-run-${p.id}`}
                    title="Run this prompt in the Ask AI box"
                    className="inline-flex items-center gap-1.5 rounded px-3 py-2 text-[11px] font-bold bg-emerald-500 hover:bg-emerald-400 text-slate-900 transition">
                    <PlayCircle className="w-3.5 h-3.5" /> Run
                  </button>
                )}
                <button onClick={() => copy(p)}
                  data-testid={`commerce-prompts-copy-${p.id}`}
                  className={`inline-flex items-center gap-1.5 rounded px-3 py-2 text-[11px] font-bold transition ${copiedId === p.id ? "bg-emerald-500 text-slate-900" : "bg-sky-500 hover:bg-sky-400 text-slate-900"}`}>
                  {copiedId === p.id ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copiedId === p.id ? "Copied" : "Copy prompt"}
                </button>
                {!p.system && (
                  <button onClick={() => remove(p)}
                    data-testid={`commerce-prompts-delete-${p.id}`}
                    className="inline-flex items-center gap-1 text-[11px] text-rose-700 hover:text-rose-200 border border-[#ebe4d3] hover:border-[#c8bd9c] rounded px-3 py-2">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="md:col-span-2 rounded-2xl border border-[#ebe4d3] bg-white shadow-sm p-8 text-center text-slate-500">
            No prompts in this category yet.
          </div>
        )}
      </div>
    </div>
  );
}


// ============================================================================
// Guided Tour — 60-second walk-through of all 14 Commerce Portal tabs
// ============================================================================
const TOUR_STEPS = [
  { tab: null, title: "Welcome to AI Commerce", body: "In the next 60 seconds we'll walk you through the 14 workspaces that power your pricing, promotion, inventory and ad-spend decisions. Use the arrows to move — you can skip any time." },
  { tab: "overview", title: "1 · Overview", body: "Your daily command deck: revenue, margin, top SKUs, stock risks — plus an Ask AI box that answers questions grounded in your live data." },
  { tab: "actions", title: "2 · Action Center", body: "Every AI recommendation lands here as an approve / reject / edit card. Guardrails, one-tap execute, WhatsApp approvals, and publish rollback all live in this queue." },
  { tab: "inventory", title: "3 · Inventory", body: "Days-of-supply, dead stock and reorder alerts across every SKU — sortable, filterable, and cross-linked into recommendations." },
  { tab: "forecasts", title: "4 · Forecasts", body: "Per-SKU demand curves for the next 30/60/90 days so you plan promos and buys before stockouts hit." },
  { tab: "bundles", title: "5 · Bundles", body: "AI-detected pairs & trios that customers buy together — one click to publish a bundled offer." },
  { tab: "products", title: "6 · Products", body: "Your unified catalog. Import from CSV, Shopify or Woo. Prices, costs and margins in one grid." },
  { tab: "competitors", title: "7 · Competitors", body: "Track competitor URLs and images (Gemini vision match), get delta-alerts, feed pricing recs automatically." },
  { tab: "regions", title: "8 · Region Gaps", body: "Location-aware pricing: where you're leaving margin on the table region-by-region, postcode-by-postcode." },
  { tab: "ads", title: "9 · Ad Spend", body: "Google & Meta ad spend fused with sales for true ACoS/ROAS margins — so recs are ad-cost aware." },
  { tab: "guardrails", title: "10 · Rules & Guardrails", body: "Set your min-margin, max-discount, approval thresholds and autonomous-publish toggle — the AI plays inside your rules." },
  { tab: "team", title: "11 · Team & Roles", body: "Invite teammates, assign approver roles, route recs to the right owner." },
  { tab: "prompts", title: "12 · Prompt Library", body: "11 ready-to-run business questions plus your own saved templates. Tap Run to fire any prompt straight into the Ask AI box." },
  { tab: "backtest", title: "13 · Backtesting", body: "Replay past weeks with today's AI — see what it would've suggested and how it would've performed." },
  { tab: "audit", title: "14 · Audit Log", body: "Every AI decision, every approval, every publish and rollback — timestamped, signed, exportable." },
  { tab: "report", title: "Weekly Report", body: "The client-ready digest: print to PDF, or push to Slack as a rich Block-Kit card with approve buttons." },
  { tab: null, title: "You're set!", body: "Hit Re-analyse & generate any time to have the AI produce fresh recommendations. Toggle Compact for denser rows. See you in the workspace." },
];

function GuidedTour({ onClose, onStep }) {
  const [i, setI] = useState(0);
  const step = TOUR_STEPS[i];
  useEffect(() => { onStep && onStep(step.tab); }, [i]); // eslint-disable-line
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") setI((v) => Math.min(TOUR_STEPS.length - 1, v + 1));
      else if (e.key === "ArrowLeft") setI((v) => Math.max(0, v - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const isLast = i === TOUR_STEPS.length - 1;
  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-900/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      data-testid="commerce-tour-overlay"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-[#ebe4d3] bg-white shadow-2xl overflow-hidden"
        data-testid="commerce-tour-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#f0e9d6] bg-[#faf5e8]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 grid place-items-center">
              <HelpCircle className="w-4 h-4 text-white" />
            </div>
            <div className="text-[11px] uppercase tracking-widest text-slate-600 font-bold">
              Portal tour · {i + 1} of {TOUR_STEPS.length}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="commerce-tour-close"
            className="rounded-lg p-1.5 text-slate-500 hover:text-slate-900 hover:bg-white transition"
            aria-label="Close tour"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-6">
          <div className="text-lg font-bold text-slate-900" data-testid="commerce-tour-title">{step.title}</div>
          <div className="mt-3 text-sm text-slate-700 leading-relaxed" data-testid="commerce-tour-body">{step.body}</div>
          {/* Progress bar */}
          <div className="mt-5 h-1.5 rounded-full bg-[#f0e9d6] overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-300"
              style={{ width: `${((i + 1) / TOUR_STEPS.length) * 100}%` }}
            />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-[#f0e9d6] bg-[#faf5e8] flex items-center justify-between gap-2">
          <button
            onClick={onClose}
            data-testid="commerce-tour-skip"
            className="text-xs font-semibold text-slate-500 hover:text-slate-900 px-2 py-1.5"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setI((v) => Math.max(0, v - 1))}
              disabled={i === 0}
              data-testid="commerce-tour-back"
              className="inline-flex items-center gap-1 rounded-lg border border-[#dcd3bc] hover:border-[#c8bd9c] bg-white hover:bg-[#f5efdd] px-3 py-2 text-xs font-semibold transition disabled:opacity-40"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> Back
            </button>
            {!isLast ? (
              <button
                onClick={() => setI((v) => Math.min(TOUR_STEPS.length - 1, v + 1))}
                data-testid="commerce-tour-next"
                className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900 px-4 py-2 text-xs font-bold transition"
              >
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button
                onClick={onClose}
                data-testid="commerce-tour-finish"
                className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900 px-4 py-2 text-xs font-bold transition"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Finish
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
